// IAM / admin & settings pages. Each is permission-gated by the backend; the UI
// also hides controls the caller can't use. Built on MUI v7.
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { navClickHandler } from '../utils/navClick';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Checkbox, Chip, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Divider,
  FormControlLabel, IconButton, LinearProgress, List, ListItem, ListItemText, MenuItem, Radio,
  RadioGroup, Select, Stack, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs,
  TextField, Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useColorScheme } from '@mui/material/styles';
import { LogoLockup } from '../components/branding/Logo';
import ProjectDangerDialog from '../components/dialogs/ProjectDangerDialog';
import ConfirmDialog from '../components/dialogs/ConfirmDialog';
import { IconSprite, Ic, BrandLogo } from '../components/shell/Icons';
import { useAuth } from '../contexts/AuthContext';
import iamApi from '../services/iamApi';
import useAutoFocus from '../hooks/useAutoFocus';
import { OrgPlanTab, UserUsageTab, PlatformBillingTab } from './billing';
import { requirementsApi } from '../services/api';
import { resolveApiBaseUrl } from '../services/resolveApiBaseUrl';
import { supabase, supabaseEnabled } from '../services/supabaseClient';
import { usePrefs, setPref, ACCENTS } from '../services/prefs';
import BuildInfo from '../components/BuildInfo';

// #409: item-type / field keys are machine identifiers (mirrors the backend
// `_KEY_RE` in iam_api.py). Reject spaces/punctuation client-side so the user gets
// a clear message instead of a raw 422. Returns an error string, or '' when valid.
const KEY_RE = /^[a-z][a-z0-9_]*$/;
function keyError(raw) {
  const k = (raw || '').trim().toLowerCase();
  if (!k) return 'A key is required.';
  if (!KEY_RE.test(k)) {
    return 'Key must start with a letter and contain only lowercase letters, digits and underscores (no spaces).';
  }
  return '';
}

// #355: per-client MCP setup steps for the Settings → Claude / MCP tab. Uses native
// <details> so no extra deps; each client shows the exact config/command for the RMDV
// remote (streamable-HTTP) server. Sources: code.claude.com/docs/en/mcp,
// code.visualstudio.com/docs/agents/reference/mcp-configuration, developers.openai.com/codex/mcp.
const copyToClipboard = (t) => {
  try { navigator.clipboard.writeText(t); } catch { /* clipboard may be blocked */ }
};

// Module scope, not inside McpSetupGuides' render (#943,
// react-hooks/static-components + react/no-unstable-nested-components). Defined inline,
// `Block` was a NEW component type on every render, so React unmounted and remounted all
// four <details> — discarding the open/closed state the user had just set. `copy` moved
// out with it; it touches only navigator.clipboard.
function McpBlock({ title, children, code }) {
  return (
    <details style={{ borderBottom: '1px solid var(--border, rgba(127,127,127,.25))', padding: '6px 0' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13.5 }}>{title}</summary>
      <div style={{ margin: '8px 0 4px', fontSize: 13 }}>{children}</div>
      {code && (
        <Box sx={{ position: 'relative' }}>
          <Box component="pre" sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>{code}</Box>
          <Button size="small" sx={{ position: 'absolute', top: 4, right: 4 }} onClick={() => copyToClipboard(code)}>Copy</Button>
        </Box>
      )}
    </details>
  );
}

function McpSetupGuides({ mcpUrl }) {
  return (
    <Box>
      <McpBlock
        title="Claude Code (CLI)"
        code={`claude mcp add --transport http rmdv ${mcpUrl}`}
      >
        Run the command, then in a session run <code>/mcp</code> and approve the sign-in (OAuth)
        on first use. Verify with <code>claude mcp list</code>.
      </McpBlock>
      <McpBlock
        title="VS Code (Copilot / agent mode)"
        code={`// .vscode/mcp.json\n{\n  "servers": {\n    "rmdv": { "type": "http", "url": "${mcpUrl}" }\n  }\n}`}
      >
        Create <code>.vscode/mcp.json</code> in your workspace (or run <em>MCP: Open User Configuration</em>),
        add the server, then <em>Start</em> it and approve sign-in.
      </McpBlock>
      <McpBlock
        title="OpenAI Codex (CLI / IDE)"
        code={`# ~/.codex/config.toml\n[mcp_servers.rmdv]\nurl = "${mcpUrl}"`}
      >
        Add the server to <code>~/.codex/config.toml</code> (shared by the CLI and IDE extension), then
        <code> codex mcp login rmdv</code> if prompted. If your Codex version only supports local (STDIO)
        servers, bridge the remote with <code>npx mcp-remote {mcpUrl}</code>.
      </McpBlock>
      <McpBlock
        title="Antigravity / other MCP clients"
      >
        Open the client&apos;s <em>MCP / Tools / Connectors</em> settings and add a <strong>custom / remote</strong>
        server with the URL above. If it asks for a bearer token instead of OAuth, issue a personal token
        below and paste it as the <code>Authorization: Bearer &lt;token&gt;</code> header.
      </McpBlock>
    </Box>
  );
}

// Mockup controls (settings.html): segmented button group + iOS-style switch,
// using the ds.css .seg/.switch classes.
function Seg({ value, options, onChange }) {
  return (
    <div className="seg">
      {options.map((o) => (
        <button key={o.value} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)} type="button">{o.label}</button>
      ))}
    </div>
  );
}
function Toggle({ on, onChange, disabled = false, label, 'data-testid': testId }) {
  // `label` -> aria-label (#943, jsx-a11y/control-has-associated-label). A
  // role="switch" with no accessible name is announced as "switch, on" with no
  // indication of WHAT is on, which is the whole content of the control.
  return <div
    className={`switch${on ? '' : ' off'}${disabled ? ' disabled' : ''}`}
    role="switch" aria-checked={on} aria-disabled={disabled || undefined}
    aria-label={label}
    data-testid={testId}
    tabIndex={disabled ? -1 : 0}
    style={disabled ? { opacity: 0.45, pointerEvents: 'none' } : undefined}
    onClick={() => { if (!disabled) onChange(!on); }}
    onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onChange(!on); } }}><i /></div>;
}
function LRow({ title, desc, children }) {
  return (
    <div className="lrow">
      <div><div className="t-strong">{title}</div><div className="t-body muted3" style={{ fontSize: 12 }}>{desc}</div></div>
      <div style={{ flex: 1 }} />
      {children}
    </div>
  );
}

// --- small helpers ---------------------------------------------------------
/**
 * @param {Function} fn      the loader
 * @param {Array}    deps    reload when these change
 * @param {boolean}  enabled #652: false = do not fetch. The account portals mount every
 *   tab's loader at once, so landing on Settings fired a request per tab for tabs the
 *   user could not see. Gating on the ACTIVE tab makes them lazy without restructuring
 *   the pages: the call site declares which tab needs the data.
 *
 *   Sticky by construction rather than by extra code: `data` is component state and is
 *   never cleared here, so leaving a tab keeps what was already loaded and returning to
 *   it does not re-request. `reload()` from an action handler still works — an action
 *   can only be taken on a tab that is on screen, where `enabled` is true.
 */
function useAsync(fn, deps, enabled = true) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // #818: starts `true` (a tab about to load has nothing to show yet) so callers
  // can tell "still loading" apart from "loaded and genuinely empty" — the fetch
  // that used to be indistinguishable from `data === []` and rendered an
  // "unavailable"/"no X yet" empty-state for however long the request took.
  const [loading, setLoading] = useState(true);
  // `fn` is deliberately NOT a dependency and the array is deliberately a spread of the
  // CALLER's `deps`: this is a generic loader whose invalidation contract is owned by the
  // call site (every caller passes a fresh arrow for `fn`, so depending on it would refetch
  // on every render of every account portal). ESLint cannot check either shape — a spread
  // element is not statically enumerable — so the rule can only be suppressed here, not
  // satisfied. The correctness burden sits with the ~20 `useAsync(...)` call sites below,
  // each of which lists what its own loader closes over. (The directive sits on the
  // dependency-array line because that is where the rule reports a useCallback, not on
  // the `useCallback(` line.)
  const reload = useCallback(() => {
    if (!enabled) return;
    setLoading(true);
    // #818: settle data/error AND the loading flag in the SAME handler rather than a
    // trailing `.finally()`. A finally runs one microtask later, so the loading update
    // landed after the caller's await had already returned — outside React's act() in
    // tests, and an extra render in the app. One hop, one render, one settle.
    fn().then(
      (d) => { setData(d); setLoading(false); },
      (e) => { setError(e?.response?.data?.detail || String(e)); setLoading(false); },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps || []), enabled]);
  useEffect(() => { reload(); }, [reload]);
  return { data, error, reload, setData, loading };
}

// #818: a compact inline "still loading" row/line — spinner + text, shared by every
// org-settings tab's list so a slow fetch reads as "loading", never as a premature
// "nothing here" empty-state.
function LoadingNote({ text = 'Loading…' }) {
  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', color: 'text.secondary' }}>
      <CircularProgress size={14} thickness={5} />
      <Typography variant="body2">{text}</Typography>
    </Stack>
  );
}

/**
 * #652 — tabs that are real links.
 *
 * "there are no tab anchors or real hrefs to enable middle click functionality". MUI's
 * `<Tab>` renders a `<button>`, so middle-clicking a tab did nothing and the tab was
 * not addressable by URL at all on two of the three portals. Each tab now renders as a
 * real `<a href="?tab=key">` with ONLY the plain left click intercepted (utils/navClick,
 * the #339 rule), so middle-click and ctrl/cmd-click get the browser's native new-tab
 * behaviour exactly as they would for any other link.
 *
 * The href is also what makes a tab addressable: `?tab=` is a shareable deep link, and
 * Back/Forward walk the tabs.
 */
function tabLinkProps(key, index, setTab, navigate, pathname) {
  return {
    component: 'a',
    href: `${pathname}?tab=${key}`,
    onClick: navClickHandler(() => {
      setTab(index);
      // Not `replace`: Back should return to the previous tab rather than leave the
      // portal, which is what a control that looks like a link is expected to do.
      navigate(`${pathname}?tab=${key}`);
    }),
  };
}

/**
 * Tab index synced to `?tab=<key>` for a page whose tab keys are `keys`, in order.
 * Returns `[tab, linkFor]` — `setTab` stays internal, because the ONLY way a tab
 * should change is through a link (that is the whole point of #652); handing callers
 * a setter invites a click path that changes the tab without changing the URL.
 */
function useTabParam(keys, pathname) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => {
    const i = keys.indexOf(searchParams.get('tab'));
    return i === -1 ? 0 : i;
  });
  // Follow a later same-page navigation to ?tab=… — a deep link clicked while already
  // on the page, and the browser's Back/Forward buttons.
  // `keys` is safe to list: all three callers pass a module-level constant
  // (SETTINGS_TAB_KEYS / ORG_TAB_KEYS / PLATFORM_TAB_KEYS), so its identity never
  // changes and this still only fires on a real navigation.
  useEffect(() => {
    const k = searchParams.get('tab');
    if (k && keys.includes(k)) setTab(keys.indexOf(k));
  }, [searchParams, keys]);
  const linkFor = (index) => tabLinkProps(keys[index], index, setTab, navigate, pathname);
  return [tab, linkFor];
}

/**
 * Card + CardHeader wrapper used by every settings/admin panel on this page.
 *
 * `action` carries no default, so an unannotated destructure infers it as a
 * REQUIRED prop and each of the 25 `<Section title=… >` call sites in this file
 * that omits it reports TS2741 (#968 Phase B). It is a header-slot node, and
 * MUI's own CardHeader `action` is optional.
 *
 * @param {object} props
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.children]
 * @param {React.ReactNode} [props.action]
 */
function Section({ title, children, action }) {
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader title={title} action={action} slotProps={{ title: { variant: 'h6' } }} />
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// Two-panel branded shell for the unauthenticated gate screens (login /
// onboarding / pending), built to the agreed mockup (signup.html): a brand
// panel on the left + the form on the right. Rendered outside AppShell, so we
// add the `.app {scheme}` wrapper here (ds.css tokens + dark mode) and the icon
// sprite that <Ic> resolves against.
const AUTH_FEATURES = [
  { icon: 'spark', text: 'Draft specs with AI — in-app or via MCP' },
  { icon: 'check', text: 'Decomposition & verification handled for you' },
  { icon: 'clock', text: 'Audit trail & coverage, always up to date' },
];

function AuthLayout({ title, subtitle, children, maxWidth = 880 }) {
  const { mode, systemMode } = useColorScheme();
  const resolved = (mode === 'system' ? systemMode : mode) || 'light';
  return (
    // #814: `index.html` locks `html, body, #root` to `height:100%; overflow:hidden`
    // (so the post-login shell can own ITS OWN internal scroll region,
    // `.content{overflow:auto}` under `.shell{height:100dvh;overflow:hidden}`) —
    // meaning nothing outside a `100%`-constrained box with its OWN `overflow:auto`
    // can ever scroll; anything taller just gets clipped by `#root`, invisibly. The
    // auth pages had no such region: `.app` here inherited ds.css's fixed
    // `height:100vh/100dvh` with no overflow rule, so on mobile a keyboard-shrunk
    // viewport left a taller-than-that form with nothing scrollable to reach the
    // rest of it. Fix: keep `.app` pinned to the viewport height (matching `#root`)
    // but make IT the scroll container (`overflowY:auto`), the same shell/content
    // pattern used post-login, rather than letting it grow past `#root` and get
    // clipped.
    <div className={`app ${resolved}`} style={{ height: '100dvh', overflowY: 'auto' }}>
      <IconSprite />
      <Box sx={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'background.default' }}>
        <Box
          sx={{
            width: '100%', maxWidth, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.10)',
          }}
        >
          {/* Brand panel */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', p: 4, color: '#fff', bgcolor: 'primary.main', position: 'relative' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: 18 }}>
              <span style={{ display: 'inline-grid', placeItems: 'center', width: 30, height: 30, borderRadius: 8, background: 'rgba(255,255,255,0.16)' }}>
                <BrandLogo size={22} />
              </span>
              RMDV
            </Box>
            <Typography sx={{ mt: 4, fontSize: 24, fontWeight: 700, lineHeight: 1.25 }}>
              You add the value.<br />We manage the traceability.
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Stack spacing={1.25} sx={{ mt: 4 }}>
              {AUTH_FEATURES.map((f) => (
                <Box key={f.text} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, opacity: 0.95 }}>
                  <Ic name={f.icon} sm /> {f.text}
                </Box>
              ))}
            </Stack>
            <Typography sx={{ mt: 3, fontSize: 11, opacity: 0.8 }}>
              Trusted with sensitive engineering data — SSO &amp; 2-factor by default.
            </Typography>
          </Box>
          {/* Form panel */}
          <Box sx={{ p: 4 }}>
            <Box sx={{ display: { md: 'none' }, mb: 2 }}><LogoLockup markSize={32} fontSize={24} /></Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{title}</Typography>
            {subtitle && <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>{subtitle}</Typography>}
            <Box sx={{ mt: 2 }}>{children}</Box>
          </Box>
        </Box>
      </Box>
    </div>
  );
}

// --- Login (Supabase when configured; dev-token fallback) -----------------
export function LoginPage() {
  const { setToken, authCallbackError, clearAuthCallbackError } = useAuth();
  const [tok, setTok] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [msg, setMsg] = useState(null);
  // #211: self-serve signup mode — creates a LOCKED (pending) account awaiting
  // admin approval. Newsletter is opt-IN: unchecked by default, carried to the
  // backend via the token's signup metadata (never inferred from signing up).
  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [optIn, setOptIn] = useState(false);

  // #156: same explicit redirectTo pattern #157 established for the magic link
  // (window.location.origin, not the Supabase-dashboard "Site URL" default) so the
  // OAuth callback lands back on THIS deployment's own origin — dev and prod are
  // different hosts (see CLAUDE.md). AuthContext's onAuthStateChange listener +
  // supabaseClient's captureAuthRedirectError adopt the resulting session / surface
  // a failed redirect (denied consent, misconfigured provider) generically, same as
  // every other auth path here — nothing provider-specific needed beyond this call.
  const oauth = (provider) => supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo: window.location.origin },
  });
  const password = async () => {
    const { error } = await supabase.auth.signInWithPassword({ email, password: pw });
    if (error) setMsg(error.message);
  };
  const signup = async () => {
    const { error } = await supabase.auth.signUp({
      email,
      password: pw,
      options: { data: { newsletter_opt_in: optIn } },
    });
    setMsg(error
      ? error.message
      : 'Account created. Confirm your email if asked, then sign in — your account will be reviewed by an administrator before you can start.');
    if (!error) { setMode('signin'); setPw(''); }
  };
  const magic = async () => {
    // #157: explicit emailRedirectTo (rather than relying on the Supabase-dashboard
    // "Site URL" default) so the link lands back on THIS deployment's own origin —
    // dev and prod are different hosts (see CLAUDE.md). AuthGate + AuthContext's
    // session listener take it from there generically, same as the OAuth buttons.
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    setMsg(error ? error.message : 'Check your email for the sign-in link.');
  };
  const forgotPassword = async () => {
    if (!email) { setMsg('Enter your email above first, then click "Forgot password?".'); return; }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/account/settings`,
    });
    setMsg(error ? error.message : 'Check your email for a link to reset your password.');
  };

  return (
    <AuthLayout
      title={mode === 'signup' ? 'Create your account' : 'Sign in'}
      subtitle={supabaseEnabled
        ? (mode === 'signup'
          ? 'New accounts are reviewed by an administrator before first use.'
          : 'Use your account to continue.')
        : 'Local development sign-in.'}
    >
      {authCallbackError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearAuthCallbackError} data-testid="auth-callback-error">
          {authCallbackError}
        </Alert>
      )}
      {msg && <Alert sx={{ mb: 2 }}>{msg}</Alert>}
      {supabaseEnabled ? (
        mode === 'signup' ? (
          <Stack spacing={2}>
            <TextField size="small" label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField label="Password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
            <FormControlLabel
              control={<Checkbox checked={optIn} onChange={(e) => setOptIn(e.target.checked)}
                slotProps={{ input: { 'data-testid': 'newsletter-opt-in' } }} />}
              label="Email me occasional product news and updates (optional)"
            />
            <Button variant="contained" disabled={!email || !pw} onClick={signup} data-testid="signup-submit">
              Create account
            </Button>
            <Button size="small" onClick={() => { setMode('signin'); setMsg(null); }}>
              Already have an account? Sign in
            </Button>
          </Stack>
        ) : (
        <Stack spacing={2}>
          {/* #156: Google / Microsoft(azure) / GitHub — provider ids per Supabase Auth's
              signInWithOAuth. Each provider still needs its client id/secret + redirect
              allow-list configured in the Supabase dashboard (owner action, not code) before
              the button actually completes a sign-in; unconfigured providers surface their
              error the same way any failed redirect does (authCallbackError above). */}
          <Button variant="outlined" onClick={() => oauth('google')}>Continue with Google</Button>
          <Button variant="outlined" onClick={() => oauth('azure')}>Continue with Microsoft</Button>
          <Button variant="outlined" onClick={() => oauth('github')}>Continue with GitHub</Button>
          <Divider>or email</Divider>
          <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <Button variant="contained" disabled={!email || !pw} onClick={password}>Sign in</Button>
          <Button disabled={!email} onClick={magic}>Email me a magic link</Button>
          <Button size="small" disabled={!email} onClick={forgotPassword}>Forgot password?</Button>
          <Button size="small" onClick={() => { setMode('signup'); setMsg(null); }} data-testid="goto-signup">
            New here? Create an account
          </Button>
        </Stack>
        )
      ) : (
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Supabase isn&apos;t configured, so this is the developer sign-in. Paste a
            Supabase-format JWT (generate one with
            {' '}<code>scripts/dev_bootstrap_user.py</code>) and continue.
          </Typography>
          <TextField
            label="Developer token (JWT)"
            value={tok}
            onChange={(e) => setTok(e.target.value)}
            multiline
            minRows={3}
          />
          <Button variant="contained" size="large" disabled={!tok} onClick={() => setToken(tok.trim())}>
            Continue
          </Button>
        </Stack>
      )}
    </AuthLayout>
  );
}

// --- 2FA sign-in challenge (#158) -------------------------------------------
// Rendered by App.js's AuthGate when the Supabase session is at aal1 but the
// account has a verified TOTP factor (aal2 required) — see AuthContext's
// `mfaRequired`/`refreshMfaStatus`. Mirrors Supabase's documented MFA flow:
// list the user's factors to find the verified TOTP one, challenge it, then
// verify the 6-digit code the authenticator app shows. A successful verify()
// fires 'MFA_CHALLENGE_VERIFIED' through onAuthStateChange, which
// AuthContext already listens to and uses to flip mfaRequired back off; we
// also call refreshMfaStatus() directly here so the gate clears even in a
// client/test double that doesn't emit that event.
export function MfaChallengePage() {
  const { logout, refreshMfaStatus } = useAuth();
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const verify = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors();
      if (factorsError) throw factorsError;
      const factor = (factorsData?.totp || []).find((f) => f.status === 'verified');
      if (!factor) {
        setMsg('No authenticator app is set up for this account. Contact an administrator.');
        return;
      }
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: factor.id });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factor.id,
        challengeId: challengeData.id,
        code: code.trim(),
      });
      if (verifyError) throw verifyError;
      await refreshMfaStatus();
    } catch (e) {
      setMsg(e?.message || 'Could not verify the code.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Two-factor authentication" subtitle="Enter the 6-digit code from your authenticator app.">
      {msg && <Alert severity="error" sx={{ mb: 2 }} data-testid="mfa-challenge-error">{msg}</Alert>}
      <Stack spacing={2}>
        <TextField
          label="6-digit code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6, 'data-testid': 'mfa-challenge-code' } }}
        />
        <Button variant="contained" disabled={code.trim().length !== 6 || busy} onClick={verify} data-testid="mfa-challenge-submit">
          Verify
        </Button>
        <Button size="small" onClick={logout}>Sign in as someone else</Button>
      </Stack>
    </AuthLayout>
  );
}

// --- Onboarding (create org / accept invite) ------------------------------
export function OnboardingPage() {
  const { refresh, chooseOrg } = useAuth();
  const [name, setName] = useState('');
  const [invite, setInvite] = useState('');
  const [msg, setMsg] = useState(null);
  const createOrg = async () => {
    const org = await iamApi.createOrg(name);
    chooseOrg(org.id);
    await refresh();
    setMsg(`Created ${org.name}`);
  };
  const accept = async () => {
    try { const r = await iamApi.acceptInvite(invite); chooseOrg(r.org_id); await refresh(); setMsg('Joined org'); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Invalid invite'); }
  };
  return (
    <AuthLayout
      title="Create your organization"
      subtitle="Set up an organization to start managing requirements, or join one with an invite."
    >
      {msg && <Alert sx={{ mb: 2 }} severity="success">{msg}</Alert>}
      <Stack spacing={3}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">New organization</Typography>
          <Stack direction="row" spacing={1}>
            <TextField fullWidth size="small" label="Organization name" value={name} onChange={(e) => setName(e.target.value)} />
            <Button variant="contained" disabled={!name} onClick={createOrg}>Create</Button>
          </Stack>
        </Stack>
        <Divider>or</Divider>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Accept an invitation</Typography>
          <Stack direction="row" spacing={1}>
            <TextField fullWidth size="small" label="Invite token" value={invite} onChange={(e) => setInvite(e.target.value)} />
            <Button variant="outlined" disabled={!invite} onClick={accept}>Join</Button>
          </Stack>
        </Stack>
      </Stack>
    </AuthLayout>
  );
}

export function PendingApproval() {
  const { user, logout } = useAuth();
  return (
    <AuthLayout title="Awaiting approval">
      <Typography sx={{ color: 'text.secondary' }}>
        Thanks for signing up{user?.email ? `, ${user.email}` : ''}. Your account is pending
        approval. You&apos;ll get access once an administrator approves it.
      </Typography>
      <Button sx={{ mt: 3 }} fullWidth onClick={logout}>Sign out</Button>
    </AuthLayout>
  );
}

// LLM providers a user/org can pick when ADDING a new stored key.
// #1042/#778: GitHub Models is retired frontend-side — it stays OUT of this
// selectable list, but the backend keeps accepting it (iam_api._KNOWN_PROVIDERS
// is unchanged, deliberately — this issue is frontend-only) so a key stored
// against it before retirement still just renders as its raw provider id in
// the lists below (ListItemText/TableCell render `k.provider` directly — no
// lookup that could throw on an id no longer in this array) and can still be
// deleted.
const LLM_PROVIDERS = ['azure_openai', 'openrouter', 'dummy'];


// --- Two-factor authentication (#158) ---------------------------------------
// Settings → Profile enrolment/un-enrolment for a Supabase TOTP factor. Uses
// supabase.auth.mfa.* directly (Supabase-native — no custom schema/store):
// enroll() returns a QR code + secret to scan into an authenticator app, then
// challenge()+verify() confirms the first 6-digit code and activates the
// factor. unenroll() removes it. Supabase's TOTP MFA has no built-in recovery
// codes; a lost device is recovered via the normal email/account-recovery
// path (surfaced below rather than fabricating a non-Supabase-native recovery
// store — see the #158 report for the deferred design decision).
function TwoFactorSection() {
  const [factors, setFactors] = useState(null); // null = still loading
  const [msg, setMsg] = useState(null);
  const [enrollData, setEnrollData] = useState(null); // { id, totp: { qr_code, secret } }
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const loadFactors = useCallback(() => {
    supabase.auth.mfa.listFactors().then(({ data, error }) => {
      if (error) { setMsg({ severity: 'error', text: error.message }); return; }
      setFactors(data?.totp || []);
    });
  }, []);
  useEffect(() => { loadFactors(); }, [loadFactors]);

  const startEnroll = async () => {
    setMsg(null);
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (error) { setMsg({ severity: 'error', text: error.message }); return; }
    setEnrollData(data);
  };

  const cancelEnroll = () => { setEnrollData(null); setCode(''); setMsg(null); };

  const confirmEnroll = async () => {
    if (!enrollData) return;
    setBusy(true);
    setMsg(null);
    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId: enrollData.id });
      if (challengeError) throw challengeError;
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: enrollData.id,
        challengeId: challenge.id,
        code: code.trim(),
      });
      if (verifyError) throw verifyError;
      setMsg({ severity: 'success', text: 'Two-factor authentication is now enabled.' });
      setEnrollData(null);
      setCode('');
      loadFactors();
    } catch (e) {
      setMsg({ severity: 'error', text: e?.message || 'Could not verify the code.' });
    } finally {
      setBusy(false);
    }
  };

  const unenroll = async (factorId) => {
    setMsg(null);
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) { setMsg({ severity: 'error', text: error.message }); return; }
    setMsg({ severity: 'success', text: 'Two-factor authentication is now disabled.' });
    loadFactors();
  };

  const activeFactor = (factors || []).find((f) => f.status === 'verified');

  return (
    <Section title="Two-factor authentication">
      {msg && <Alert sx={{ mb: 2 }} severity={msg.severity} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {activeFactor ? (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            An authenticator app is set up for your account
            {activeFactor.friendly_name ? ` (${activeFactor.friendly_name})` : ''}. You&apos;ll be asked
            for a code from it each time you sign in.
          </Typography>
          <Button color="error" variant="outlined" onClick={() => unenroll(activeFactor.id)} data-testid="mfa-unenroll">
            Turn off 2FA
          </Button>
        </>
      ) : enrollData ? (
        <Stack spacing={2} sx={{ maxWidth: 360 }}>
          <Typography variant="body2">
            Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy…),
            then enter the 6-digit code it shows to confirm.
          </Typography>
          {enrollData.totp?.qr_code && (
            <Box component="img" src={enrollData.totp.qr_code} alt="2FA setup QR code"
              sx={{ width: 180, height: 180, alignSelf: 'center' }} />
          )}
          <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
            Can&apos;t scan? Enter this key manually: <code>{enrollData.totp?.secret}</code>
          </Typography>
          <TextField
            size="small"
            label="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 6, 'data-testid': 'mfa-enroll-code' } }}
          />
          <Stack direction="row" spacing={1}>
            <Button variant="contained" disabled={code.trim().length !== 6 || busy} onClick={confirmEnroll} data-testid="mfa-enroll-confirm">
              Confirm &amp; enable
            </Button>
            <Button onClick={cancelEnroll}>Cancel</Button>
          </Stack>
        </Stack>
      ) : (
        <>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Add an authenticator app for an extra sign-in step. If you ever lose access to your
            device, recover your account via the normal email-based account-recovery path — RMDV
            does not currently issue separate backup/recovery codes for authenticator 2FA.
          </Typography>
          <Button variant="contained" onClick={startEnroll} data-testid="mfa-enroll-start">
            Set up authenticator app (2FA)
          </Button>
        </>
      )}
    </Section>
  );
}

// --- User settings ---------------------------------------------------------
// #405: deep-link a specific Settings tab via ?tab=<key> (e.g. Getting Started's
// "Go to Claude / MCP settings" → /account/settings?tab=mcp). Order must match the
// <Tab> order below.
const SETTINGS_TAB_KEYS = ['profile', 'display', 'ai', 'keys', 'mcp', 'usage', 'archived'];

export function UserSettings() {
  const { user, activeOrg, activeProject } = useAuth();
  const { mode, setMode } = useColorScheme();
  const prefs = usePrefs();
  const [tab, tabLink] = useTabParam(SETTINGS_TAB_KEYS, '/account/settings');
  // #652: each loader is gated on the tab(s) that actually RENDER its data, so landing
  // on Settings no longer fires every tab's request at once. The tab lists next to each
  // one are the tabs where that data appears — checked against the render blocks below,
  // not guessed, because gating a loader on the wrong tab shows an empty panel.
  const settings = useAsync(() => iamApi.settings(), [],
    tab === 0 || tab === 1 || tab === 2);              // profile, display (theme), ai
  // #349: archived (soft-deleted) items in scope, for the "Archived items" recovery tab.
  const archived = useAsync(() => requirementsApi.listArchived(activeProject || null),
    [activeProject], tab === 6);
  const [restoreMsg, setRestoreMsg] = useState(null);
  const restoreItem = (id) => {
    setRestoreMsg(null);
    requirementsApi.unarchiveItem(id)
      .then(() => { archived.reload?.(); setRestoreMsg({ severity: 'success', text: 'Item restored.' }); })
      .catch((e) => setRestoreMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not restore the item.' }));
  };
  // #658: "unarchive all children" — the admin-panel counterpart to the item-detail
  // cascade-archive warning. `{ id, count } | null`: set once the preflight count
  // comes back, which is what drives the confirm dialog open — a leaf/no-descendant
  // item skips the dialog and restores straight away (same "don't ask when there's
  // nothing to warn about" rule the archive-side modal already follows).
  const [unarchiveConfirm, setUnarchiveConfirm] = useState(null);
  const restoreWithChildren = (id) => {
    setRestoreMsg(null);
    requirementsApi.getUnarchiveImpact(id)
      .then((impact) => {
        const n = impact?.archived_descendant_count || 0;
        if (n > 0) setUnarchiveConfirm({ id, count: n });
        else restoreItem(id);
      })
      .catch(() => setRestoreMsg({ severity: 'error', text: 'Could not check for archived child items.' }));
  };
  const confirmRestoreWithChildren = () => {
    if (!unarchiveConfirm) return;
    const { id } = unarchiveConfirm;
    setUnarchiveConfirm(null);
    setRestoreMsg(null);
    requirementsApi.unarchiveItem(id, undefined, { cascade: true })
      .then((res) => {
        archived.reload?.();
        const n = (res && res.cascaded_ids && res.cascaded_ids.length) || 0;
        setRestoreMsg({
          severity: 'success',
          text: n > 0 ? `Item restored along with ${n} child item${n === 1 ? '' : 's'}.` : 'Item restored.',
        });
      })
      .catch((e) => setRestoreMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not restore the item.' }));
  };
  const keys = useAsync(() => iamApi.providerKeys(), [], tab === 3);       // API keys
  const tokens = useAsync(() => iamApi.mcpTokens(), [], tab === 4);        // Claude / MCP
  // Projects the user may view — source for the "Visible projects" tree scope picker.
  // Display (the visible-projects scope picker) AND Claude/MCP (the mint scope picker).
  const myProjects = useAsync(() => (activeOrg ? iamApi.myProjects(activeOrg) : Promise.resolve([])),
    [activeOrg], tab === 1 || tab === 4);
  const visibleProjects = prefs.visibleProjects || [];
  const toggleVisibleProject = (pid) => {
    const cur = prefs.visibleProjects || [];
    setPref('visibleProjects', cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]);
  };
  const [form, setForm] = useState({ theme: '', default_model: '' });
  // #1042: default to a still-selectable provider now that GitHub Models is
  // retired from this list (LLM_PROVIDERS, above).
  const [pk, setPk] = useState({ provider: 'openrouter', key: '' });

  const [pkMsg, setPkMsg] = useState(null);
  const [issued, setIssued] = useState(null);
  // #519: MCP token issuance scope — an array of project ids (same convention as
  // prefs.visibleProjects, prefs.js:23): [] = unscoped (every project the principal may
  // reach, today's behaviour). read_only denies write tools on the minted token.
  const [mintProjectIds, setMintProjectIds] = useState([]);
  const [mintReadOnly, setMintReadOnly] = useState(false);
  const toggleMintProject = (pid) => {
    setMintProjectIds((cur) => (cur.includes(pid) ? cur.filter((x) => x !== pid) : [...cur, pid]));
  };
  // #519 item 4: id -> display label for the scope-audit chips in the token list
  // (reuses myProjects, same source the mint picker above already loads).
  const projectLabel = (pid) => {
    const p = (myProjects.data || []).find((mp) => mp.id === pid);
    return p?.name || p?.title || pid;
  };
  const [pwForm, setPwForm] = useState({ next: '', confirm: '' });
  const [pwMsg, setPwMsg] = useState(null);
  const changePassword = async () => {
    setPwMsg(null);
    if (pwForm.next.length < 8) { setPwMsg({ severity: 'error', text: 'Password must be at least 8 characters.' }); return; }
    if (pwForm.next !== pwForm.confirm) { setPwMsg({ severity: 'error', text: 'Passwords do not match.' }); return; }
    const { error } = await supabase.auth.updateUser({ password: pwForm.next });
    if (error) { setPwMsg({ severity: 'error', text: error.message }); return; }
    setPwForm({ next: '', confirm: '' });
    setPwMsg({ severity: 'success', text: 'Password updated.' });
  };
  // The MCP server is mounted on the BACKEND app at /mcp (main.py `app.mount("/mcp")`),
  // reachable only at the backend origin (…onrender.com/mcp) — verified live. NOTE: the
  // branded SPA domain (dev.rmdv/rmdv.envisionengineering.co.uk) is a static host with no
  // /mcp (or /api) proxy — any path there returns index.html — so it CANNOT be advertised
  // as the MCP URL until a reverse proxy for /mcp is added. Derive from the API base
  // (resolveApiBaseUrl ends with /api) → backend origin + /mcp.
  const mcpUrl = `${resolveApiBaseUrl().replace(/\/api\/?$/, '')}/mcp`;

  useEffect(() => { if (settings.data) setForm({ theme: settings.data.theme || '', default_model: settings.data.default_model || '' }); }, [settings.data]);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Settings</Typography>
      {/* #652: each tab is a real <a href="?tab=key"> — see tabLinkProps. `onChange` is
          left off deliberately: the per-tab onClick already sets the tab AND pushes the
          URL, and having both would double-handle a plain click. */}
      <Tabs
        value={tab}
        sx={{ mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label="Profile" {...tabLink(0)} />
        <Tab label="Display" {...tabLink(1)} />
        <Tab label="AI" {...tabLink(2)} />
        <Tab label="API keys" {...tabLink(3)} />
        <Tab label="Claude / MCP" {...tabLink(4)} />
        <Tab label="Usage" {...tabLink(5)} />
        <Tab label="Archived" {...tabLink(6)} />
      </Tabs>
      {tab === 0 && (
        <>
          <Section title="Profile">
            <Typography>Email: {user?.email}</Typography>
            <Typography>Name: {user?.display_name || '—'}</Typography>
            <Typography component="span" sx={{ display: 'block' }}>Status: <Chip size="small" label={user?.status} /></Typography>
          </Section>
          {supabaseEnabled && (
            <Section title="Change password">
              {pwMsg && <Alert sx={{ mb: 2 }} severity={pwMsg.severity}>{pwMsg.text}</Alert>}
              <Stack spacing={2} sx={{ maxWidth: 360 }}>
                <TextField size="small" label="New password" type="password" value={pwForm.next}
                  onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} />
                <TextField size="small" label="Confirm new password" type="password" value={pwForm.confirm}
                  onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
                <Button variant="contained" disabled={!pwForm.next || !pwForm.confirm} onClick={changePassword} sx={{ alignSelf: 'flex-start' }}>
                  Update password
                </Button>
              </Stack>
            </Section>
          )}
          {supabaseEnabled && <TwoFactorSection />}
          <BuildInfo />
        </>
      )}
      {tab === 1 && (
        <>
          <div className="cardp" style={{ marginBottom: 14 }}>
            <div className="t-sec" style={{ marginBottom: 6 }}>Appearance</div>
            <LRow title="Theme" desc="Light is the default; dark is a faithful sibling.">
              <Seg
                value={mode || 'system'}
                options={[{ value: 'light', label: 'Light' }, { value: 'dark', label: 'Dark' }, { value: 'system', label: 'System' }]}
                onChange={(v) => { setMode(v); setForm((f) => ({ ...f, theme: v })); iamApi.saveSettings({ ...form, theme: v }).then(settings.reload).catch(() => { /* ignore save errors */ }); }}
              />
            </LRow>
            <LRow title="Accent" desc="Used for primary actions & active state.">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* #793 H: these are toggle controls, so they are real <button>s now —
                    the platform supplies the role, the tab stop and Enter/Space for
                    free. As `<span onClick>` swatches they were unreachable by keyboard
                    AND unnamed for a screen reader (colour was the only cue, which is
                    also a WCAG 1.4.1 use-of-colour problem); `aria-label` + `aria-pressed`
                    now name the option and announce which one is selected. The style
                    block gains the UA reset a native button needs (appearance/border/
                    padding) so the 20px circle renders exactly as before. */}
                {Object.entries(ACCENTS).map(([key, hsl]) => (
                  <button
                    key={key}
                    type="button"
                    title={key}
                    aria-label={`Accent colour: ${key}`}
                    aria-pressed={prefs.accent === key}
                    onClick={() => setPref('accent', key)}
                    style={{
                      width: 20, height: 20, borderRadius: '50%', background: hsl, cursor: 'pointer',
                      appearance: 'none', border: 'none', padding: 0, flex: '0 0 auto',
                      boxShadow: prefs.accent === key ? `0 0 0 2px var(--base), 0 0 0 4px ${hsl}` : 'none',
                    }}
                  />
                ))}
              </div>
            </LRow>
            <LRow title="Density" desc="Compact suits dense requirement trees.">
              <Seg
                value={prefs.density}
                options={[{ value: 'compact', label: 'Compact' }, { value: 'comfortable', label: 'Comfortable' }]}
                onChange={(v) => setPref('density', v)}
              />
            </LRow>
            <LRow title="Reduce motion" desc="Minimise animations.">
              <Toggle label="Reduce motion" on={prefs.reduceMotion} onChange={(v) => setPref('reduceMotion', v)} />
            </LRow>
          </div>
          <div className="cardp">
            <div className="t-sec" style={{ marginBottom: 6 }}>Requirements display</div>
            <LRow title="Show human IDs (REQ-0001)" desc="Prefer readable IDs over raw UUIDs.">
              <Toggle label="Show human IDs" on={prefs.showHumanIds} onChange={(v) => setPref('showHumanIds', v)} />
            </LRow>
            {/* F-21 / REQ-6107 (#581, F5): the dot has driven the APPROVAL rollup
                since #569, not the retired "verification" attribute — this label
                was never updated to match. */}
            <LRow title="Status dots in tree" desc="Approval state at a glance.">
              <Toggle label="Status dots in tree" on={prefs.statusDots} onChange={(v) => setPref('statusDots', v)} />
            </LRow>
            <LRow title="Assistant panel" desc="Show the AI panel by default on desktop.">
              <Toggle label="Assistant panel" on={prefs.assistantPanel} onChange={(v) => setPref('assistantPanel', v)} />
            </LRow>
          </div>
          <div className="cardp" style={{ marginTop: 14 }}>
            <div className="t-sec" style={{ marginBottom: 6 }}>Visible projects</div>
            <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
              Choose which of your projects appear in the requirements tree. Select none to
              show every project you can access. When more than one is shown, the tree groups
              them under top-level project nodes.
            </Typography>
            {myProjects.loading && !myProjects.data ? (
              <LoadingNote text="Loading projects…" />
            ) : (myProjects.data || []).length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No projects yet — all requirements are shown. An org admin grants project
                access under Project configuration.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 2 }}>
                {(myProjects.data || []).map((p) => {
                  const on = visibleProjects.includes(p.id);
                  return (
                    <Chip
                      key={p.id}
                      label={p.name || p.title || p.id}
                      color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      onClick={() => toggleVisibleProject(p.id)}
                      data-testid={`visible-project-${p.id}`}
                    />
                  );
                })}
              </Stack>
            )}
            <LRow title="Project label prefix" desc="Shown before each project name when grouping (e.g. PRJ-).">
              <TextField
                size="small"
                value={prefs.projectPrefix}
                onChange={(e) => setPref('projectPrefix', e.target.value)}
                sx={{ maxWidth: 140 }}
                slotProps={{ htmlInput: { 'data-testid': 'project-prefix-input' } }}
              />
            </LRow>
          </div>
        </>
      )}
      {tab === 2 && (
        <Section title="AI / agent" action={<Button onClick={() => iamApi.saveSettings(form).then(settings.reload)}>Save</Button>}>
          <TextField size="small" label="Default model" value={form.default_model} onChange={(e) => setForm({ ...form, default_model: e.target.value })} />
        </Section>
      )}
      {tab === 3 && (
        <Section title="API keys & provider overrides">
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Your own LLM key for a provider. When set, your AI requests run on it instead of
            your organization&apos;s key or the platform default. Keys are stored encrypted and
            never shown again.
          </Typography>
          {pkMsg && <Alert sx={{ mb: 2 }} severity={pkMsg.severity} onClose={() => setPkMsg(null)}>{pkMsg.text}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
            <Select size="small" value={pk.provider} onChange={(e) => setPk({ ...pk, provider: e.target.value })} data-testid="user-key-provider">
              {LLM_PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
            <TextField size="small" label="API key" type="password" value={pk.key} onChange={(e) => setPk({ ...pk, key: e.target.value })} />
            <Button variant="contained" disabled={!pk.key}
              onClick={() => iamApi.addProviderKey(pk)
                .then(() => { setPk({ ...pk, key: '' }); setPkMsg({ severity: 'success', text: 'Key saved.' }); keys.reload(); })
                .catch((e) => setPkMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not save the key.' }))}>Add</Button>
          </Stack>
          <List dense>
            {(keys.data || []).map((k) => (
              <ListItem key={k.id} secondaryAction={<IconButton onClick={() => iamApi.delProviderKey(k.id).then(keys.reload)}><DeleteIcon /></IconButton>}>
                <ListItemText primary={k.provider} secondary={k.endpoint_override || 'default endpoint'} />
              </ListItem>
            ))}
          </List>
        </Section>
      )}
      {tab === 4 && (
        <Section title="Link Claude / ChatGPT (MCP)">
          <Typography variant="body2" sx={{ mb: 1 }}>
            Add RMDV as a connector so Claude can read and update your requirements from chat.
            Click <strong>Add to Claude</strong>, then in Claude paste the server URL and sign in to approve.
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mb: 2, wordBreak: 'break-all', color: 'text.secondary' }}>
            MCP server URL: <code>{mcpUrl}</code>
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button variant="contained" onClick={() => {
              try { navigator.clipboard.writeText(mcpUrl); } catch { /* clipboard may be blocked */ }
              // Claude's custom-connector UI lives under Customize → Connectors. The extra
              // query params are a best-effort pre-fill of the "Add custom connector" modal;
              // unknown params are ignored, so this is no worse than opening the page plain.
              const deepLink = `https://claude.ai/customize/connectors?modal=add-custom-connector&connectorName=${encodeURIComponent('RMDV')}&connectorUrl=${encodeURIComponent(mcpUrl)}`;
              window.open(deepLink, '_blank', 'noopener');
              setIssued(`Opened Claude → Customize → Connectors in a new tab. If the "Add custom connector" dialog isn't pre-filled, click the "+" next to Connectors, choose "Add custom connector", paste the copied URL (${mcpUrl}), click Add, then sign in to approve.`);
            }}>Add to Claude</Button>
            <Button variant="outlined" onClick={() => {
              try { navigator.clipboard.writeText(mcpUrl); setIssued(`Copied: ${mcpUrl}`); } catch { setIssued(mcpUrl); }
            }}>Copy URL</Button>
          </Stack>
          {issued && <Alert sx={{ mt: 1, mb: 1 }} severity="info" data-testid="mcp-token-result-alert">{issued}</Alert>}

          <Divider sx={{ my: 2 }} />
          {/* #355: practical setup for popular clients + an example prompt + a downloadable
              basic instruction set. */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Set up in your tool</Typography>
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            RMDV is a remote (streamable-HTTP) MCP server at <code style={{ wordBreak: 'break-all' }}>{mcpUrl}</code>.
            Most clients authenticate via the built-in OAuth flow; clients that take a bearer token
            directly can use a personal token (issue one below).
          </Typography>
          <McpSetupGuides mcpUrl={mcpUrl} />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Example prompt</Typography>
          <Box component="pre" sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', fontSize: 12.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
{`Using the RMDV tools, create a project called "Demo", then build a small V-model:
a Stakeholder Needs container with one need, a System Requirements container with one
"shall" requirement that decomposes that need, and a test_case that verifies it. Search
first to avoid duplicates, and give every write a short reason.`}
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mt: 1 }}>
            <Button size="small" variant="outlined"
              onClick={() => { try { navigator.clipboard.writeText('Using the RMDV tools, create a project called "Demo", then build a small V-model: a Stakeholder Needs container with one need, a System Requirements container with one "shall" requirement that decomposes that need, and a test_case that verifies it. Search first to avoid duplicates, and give every write a short reason.'); setIssued('Example prompt copied.'); } catch { /* clipboard may be blocked */ } }}>
              Copy prompt
            </Button>
            <Button size="small" variant="outlined" component="a" href="/rmdv-instructions.md" download="rmdv-instructions.md">
              Download rmdv-instructions.md
            </Button>
          </Stack>
          <Alert severity="info" sx={{ mt: 2 }}>
            Additional industry- and compliance-specific instruction packs (automotive/ASPICE,
            aerospace/DO-178C, medical/IEC 62304, and more) are available with certain plans.
          </Alert>

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
            Advanced: issue a scoped personal token (only needed for clients that take a bearer token directly;
            requires MCP enabled for your org).
          </Typography>
          {/* #519: issuance-time scoping — a project multiselect (empty = unscoped, every
              project the principal may reach) + a read-only toggle, both passed straight
              through to the mint call. */}
          <Typography variant="body2" sx={{ mb: 0.5 }}>Scope (optional)</Typography>
          {myProjects.loading && !myProjects.data ? (
            <LoadingNote text="Loading projects…" />
          ) : (myProjects.data || []).length === 0 ? (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
              No projects yet — the token will be unscoped (every project you can access).
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }}>
              {(myProjects.data || []).map((p) => {
                const on = mintProjectIds.includes(p.id);
                return (
                  <Chip
                    key={p.id}
                    label={p.name || p.title || p.id}
                    color={on ? 'primary' : 'default'}
                    variant={on ? 'filled' : 'outlined'}
                    onClick={() => toggleMintProject(p.id)}
                    data-testid={`mint-project-${p.id}`}
                  />
                );
              })}
            </Stack>
          )}
          <LRow title="Read-only" desc="Denies write tools on this token.">
            <Toggle label="Read-only token" on={mintReadOnly} onChange={setMintReadOnly} data-testid="mint-read-only-toggle" />
          </LRow>
          <Button size="small" variant="outlined" disabled={!activeOrg} sx={{ mt: 1 }}
            onClick={() => iamApi.createMcpToken(activeOrg, {
              name: 'assistant',
              project_ids: mintProjectIds.length ? mintProjectIds : null,
              read_only: mintReadOnly,
            }).then((r) => { setIssued(r.token); tokens.reload(); })
              .catch((e) => setIssued(e?.response?.data?.detail || 'MCP not enabled'))}>
            Issue token
          </Button>
          {/* #519 item 4 (revoke-by-scope): one chip per project any ACTIVE token is
              scoped to — its delete (x) revokes EVERY one of the caller's own tokens
              scoped to that project in a single call, not just one token. Unscoped
              tokens never contribute here (see repo.revoke_mcp_tokens_for_project). */}
          {(() => {
            const activeScopedProjects = [...new Set(
              (tokens.data || []).filter((t) => !t.revoked_at && t.project_ids).flatMap((t) => t.project_ids),
            )];
            if (!activeScopedProjects.length) return null;
            return (
              <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', mt: 1.5, mb: 0.5, alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>Revoke all tokens for:</Typography>
                {activeScopedProjects.map((pid) => (
                  <Chip
                    key={pid}
                    size="small"
                    variant="outlined"
                    label={projectLabel(pid)}
                    onDelete={() => iamApi.revokeMcpTokensForProject(activeOrg, pid).then(tokens.reload)}
                    data-testid={`revoke-project-${pid}`}
                  />
                ))}
              </Stack>
            );
          })()}
          <List dense>
            {(tokens.data || []).map((t) => (
              <ListItem key={t.id} data-testid={`mcp-token-${t.id}`}
                secondaryAction={<Button onClick={() => iamApi.revokeMcpToken(t.id).then(tokens.reload)}>Revoke</Button>}>
                <ListItemText
                  primary={t.name}
                  slotProps={{ secondary: { component: 'div' } }}
                  secondary={
                    // #519 item 4 (scope audit): the project chips ("All projects" when
                    // unscoped, same convention as the mint picker above), a read-only
                    // badge, and expiry — never the token hash/secret.
                    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center', mt: 0.5 }}>
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                        {t.revoked_at ? 'revoked' : 'active'}
                      </Typography>
                      {t.project_ids
                        ? t.project_ids.map((pid) => (
                          <Chip key={pid} size="small" label={projectLabel(pid)} data-testid={`mcp-token-scope-${t.id}-${pid}`} />
                        ))
                        : <Chip size="small" variant="outlined" label="All projects" data-testid={`mcp-token-scope-${t.id}-all`} />}
                      {t.read_only && <Chip size="small" color="warning" label="Read-only" data-testid={`mcp-token-readonly-${t.id}`} />}
                      <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }} data-testid={`mcp-token-expiry-${t.id}`}>
                        {t.expires_at ? `expires ${new Date(t.expires_at).toLocaleDateString()}` : 'never expires'}
                      </Typography>
                    </Stack>
                  }
                />
              </ListItem>
            ))}
          </List>
        </Section>
      )}
      {tab === 5 && <Section title="My usage"><UserUsageTab /></Section>}
      {tab === 6 && (
        <Section title="Archived items">
          <Typography variant="body2" sx={{ mb: 1.5, color: 'text.secondary' }}>
            Items that were archived (soft-deleted). Restoring returns an item to the tree with its
            history intact. {activeProject ? '' : 'Showing all archived items in this organisation.'}
          </Typography>
          {restoreMsg && <Alert sx={{ mb: 2 }} severity={restoreMsg.severity} onClose={() => setRestoreMsg(null)}>{restoreMsg.text}</Alert>}
          {archived.loading && !archived.data ? (
            <LoadingNote text="Loading archived items…" />
          ) : (archived.data || []).length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>No archived items.</Typography>
          ) : (
            <Box className="scroll-x"><Table size="small">
              <TableHead><TableRow><TableCell>Item</TableCell><TableCell>Id</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
              <TableBody>
                {(archived.data || []).map((it) => (
                  <TableRow key={it.id}>
                    <TableCell>{it.title || 'Untitled'}</TableCell>
                    <TableCell><code>{it.display_id || it.id}</code></TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => restoreItem(it.id)}>Restore</Button>
                      {/* #658: "unarchive all children" — a cascade-archived container's
                          descendants are also individually archived rows in this same
                          list, so they're reachable one at a time already; this offers
                          the whole subtree back in one action instead. */}
                      <Button size="small" data-testid={`restore-with-children-${it.id}`}
                        onClick={() => restoreWithChildren(it.id)}>
                        Restore with children…
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Box>
          )}
          <ConfirmDialog
            open={!!unarchiveConfirm}
            onClose={() => setUnarchiveConfirm(null)}
            onConfirm={confirmRestoreWithChildren}
            title="Restore this item and its children?"
            message={unarchiveConfirm
              ? `This action will also restore all ${unarchiveConfirm.count} archived child item${unarchiveConfirm.count === 1 ? '' : 's'}.`
              : ''}
            severity="warning"
            confirmColor="primary"
            confirmLabel="Restore all"
            testId="unarchive-cascade-dialog"
          />
        </Section>
      )}
    </Box>
  );
}

// --- Copy project (#195 part 2) --------------------------------------------
// 'empty' duplicates the project shell (name/settings + its enabled item-type
// set) with zero requirements; 'full' additionally duplicates every
// requirement/container, preserving hierarchy and intra-project links.
function CopyProjectDialog({ open, project, onClose, onCopy }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState('empty');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // #793: rendered unconditionally with `open` toggling — a bare `autoFocus`
  // only fires on the FIRST mount, so a reopen after the first silently lost
  // focus. See hooks/useAutoFocus.
  const focusRef = useAutoFocus(open);

  useEffect(() => {
    if (open) {
      setName(project ? `${project.name || project.title || ''} (copy)` : '');
      setMode('empty');
      setErr(null);
    }
  }, [open, project]);

  const submit = () => {
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    onCopy(name.trim(), mode)
      .catch((e) => setErr(e?.response?.data?.detail || 'Could not copy the project.'))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Copy &quot;{project?.name || project?.title || ''}&quot;</DialogTitle>
      <DialogContent>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            inputRef={focusRef}
            fullWidth
            size="small"
            label="New project name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            slotProps={{ htmlInput: { 'data-testid': 'copy-project-name' } }}
          />
          <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
            <FormControlLabel value="empty" control={<Radio />}
              label="Empty copy — same settings & item types, no requirements" />
            <FormControlLabel value="full" control={<Radio />}
              label="Full copy — also duplicates every requirement, preserving structure & links" />
          </RadioGroup>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!name.trim() || busy} onClick={submit} data-testid="copy-project-submit">
          {busy ? 'Copying…' : 'Copy project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Org settings ----------------------------------------------------------
// #652: tab keys, in <Tab> order — the `?tab=` values that make each tab addressable.
const ORG_TAB_KEYS = ['members', 'projects', 'capabilities', 'keys', 'plan'];

export function OrgSettings() {
  const { activeOrg, orgs, chooseOrg, refresh, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  // #652: addressable tabs (`?tab=members`) + real anchors, and one loader per tab
  // instead of six on mount. Tab numbers below match the <Tab> order and the
  // `tab === n` render blocks further down.
  const [tab, tabLink] = useTabParam(ORG_TAB_KEYS, '/account/org');
  const members = useAsync(() => (activeOrg ? iamApi.orgMembers(activeOrg) : Promise.resolve([])), [activeOrg], tab === 0);
  const invitations = useAsync(() => (activeOrg ? iamApi.orgInvitations(activeOrg) : Promise.resolve([])), [activeOrg], tab === 0);
  const caps = useAsync(() => (activeOrg ? iamApi.orgCaps(activeOrg) : Promise.resolve([])), [activeOrg], tab === 2);
  // N-11 / REQ-6130 (#601): the org's additive `approver` grants. Loaded once for the
  // whole org rather than per member row, so the Members table renders from one fetch.
  // Non-admins get 403 here (the endpoint requires `manage_members`) — swallowed to an
  // empty list so the rest of the tab still renders, same shape as `orgKeys` above.
  const grants = useAsync(() => (activeOrg ? iamApi.roleGrants(activeOrg).catch(() => []) : Promise.resolve([])), [activeOrg], tab === 0);
  // Members too, not just Projects: the approver-scope picker in the Members tab lists
  // project names from here, and gating it on tab 1 alone would render bare ids there.
  const projects = useAsync(() => (activeOrg ? iamApi.myProjects(activeOrg) : Promise.resolve([])), [activeOrg], tab === 0 || tab === 1);
  // Org LLM keys (#210) — admin-only endpoint; non-admins just see the error state.
  const orgKeys = useAsync(() => (activeOrg ? iamApi.orgProviderKeys(activeOrg).catch(() => []) : Promise.resolve([])), [activeOrg], tab === 3);
  // #1042: default to a still-selectable provider now that GitHub Models is
  // retired from this list (LLM_PROVIDERS, above).
  const [opk, setOpk] = useState({ provider: 'openrouter', key: '', endpoint_override: '' });

  const [opkMsg, setOpkMsg] = useState(null);
  const [inv, setInv] = useState({ email: '', role: 'editor' });
  const [invMsg, setInvMsg] = useState(null);
  const [newProj, setNewProj] = useState('');
  const [projMsg, setProjMsg] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null); // project row pending archive confirmation
  // In-app confirmation modal (replaces window.confirm, #943). Holds the descriptor
  // for the pending action — { title, message, severity, confirmLabel, confirmColor,
  // testId, onConfirm } — or null when nothing is awaiting confirmation.
  const [confirm, setConfirm] = useState(null);
  const [copyTarget, setCopyTarget] = useState(null); // project row pending the copy dialog (#195 part 2)
  const [memberAction, setMemberAction] = useState(null); // { email, severity, text, secret? }
  const [busyUid, setBusyUid] = useState(null);
  const activeOrgObj = (orgs || []).find((o) => o.id === activeOrg);
  const [orgName, setOrgName] = useState(activeOrgObj?.name || '');
  const [newOrgName, setNewOrgName] = useState('');
  const [orgMsg, setOrgMsg] = useState(null);

  useEffect(() => { setOrgName(activeOrgObj?.name || ''); }, [activeOrgObj?.name]);

  // Single entry point for both the "Invite" button and "Resend" on a pending
  // invitation row — same call, same result handling either way.
  const sendInvite = (email, role) => {
    setInvMsg(null);
    iamApi.invite(activeOrg, { email, role }).then((r) => {
      members.reload?.();
      invitations.reload?.();
      // `magic_link` is a real Supabase sign-in link for this email — it works
      // even for an address that can't receive mail (e.g. a throwaway address
      // created just to hand an agent/collaborator access), unlike `accept_url`
      // (our own app route, which only does anything once the visiting browser
      // already holds a session). Prefer it whenever present.
      if (r.magic_link) {
        setInvMsg({
          severity: 'success',
          text: r.email_sent
            ? `Invitation emailed to ${r.email}. If they can't check that inbox, you can also copy this direct sign-in link and share it another way:`
            : `Invitation created for ${r.email}. Email isn't configured here, so use this direct sign-in link instead:`,
          link: r.magic_link,
        });
      } else {
        setInvMsg(r.email_sent
          ? { severity: 'success', text: `Invitation emailed to ${r.email}. They can sign in with that address to join — even after the link expires.` }
          : { severity: 'warning', text: `Invitation created for ${r.email}, but email isn't configured here. Share this link so they can join:`, link: r.accept_url });
      }
    }).catch((e) => setInvMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not send invitation.' }));
  };

  // #356: change an existing member's org role in place (previously add/remove only;
  // the only workaround was recreating the org). Backend guards `manage_roles`.
  const assignRole = (uid, role) => {
    setMemberAction(null);
    setBusyUid(uid);
    iamApi.assignOrgRole(activeOrg, uid, role)
      .then((r) => {
        members.reload?.();
        invitations.reload?.();
        // SR-14 (#319): assigning a role to someone who isn't a member yet sends a
        // consent invitation (pending) rather than attaching them directly.
        setMemberAction(r?.pending
          ? { severity: 'success', text: `Invitation sent — they'll join as ${role} once they accept.` }
          : { severity: 'success', text: `Role updated to ${role}.` });
      })
      .catch((e) => setMemberAction({ severity: 'error', text: e?.response?.data?.detail || 'Could not update the role.' }))
      .finally(() => setBusyUid(null));
  };

  // N-11 / REQ-6130 (#601) — the admin surface for the distinct `approver` role #591
  // introduced. Deliberately NOT another option in the Role select above: `role_grants`
  // is ADDITIVE and scoped, so a member holds `editor` AND `approver`, org-wide or in
  // named projects. A single-role Select cannot express that, which is the whole reason
  // `role_grants` exists alongside `memberships`.
  //
  // ORG_WIDE is the sentinel for a grant with `project_id IS NULL`. The backend treats
  // that as "authorises every project in the org" (`core/iam/approval_authority.py`), so
  // it is a scope like any other here rather than a separate control.
  const ORG_WIDE = '__org__';
  const approverScopesFor = (uid) => (grants.data || [])
    .filter((g) => g.user_id === uid && g.role === 'approver')
    .map((g) => g.project_id || ORG_WIDE);

  const setApproverScopes = (uid, next) => {
    const current = (grants.data || []).filter((g) => g.user_id === uid && g.role === 'approver');
    const wanted = new Set(next);
    const added = next.filter((s) => !current.some((g) => (g.project_id || ORG_WIDE) === s));
    const removed = current.filter((g) => !wanted.has(g.project_id || ORG_WIDE));
    if (!added.length && !removed.length) return;
    setMemberAction(null);
    setBusyUid(uid);
    Promise.all([
      ...added.map((s) => iamApi.grantRole(activeOrg, {
        userId: uid, role: 'approver', projectId: s === ORG_WIDE ? null : s,
      })),
      ...removed.map((g) => iamApi.revokeRoleGrant(activeOrg, g.id)),
    ])
      .then(() => {
        grants.reload?.();
        setMemberAction({
          severity: 'success',
          text: next.length
            ? `Approval rights updated — ${next.length} scope${next.length === 1 ? '' : 's'}.`
            : 'Approval rights removed.',
        });
      })
      // A partial failure is possible (several calls, no transaction), so reload
      // regardless: the table must show what the server actually holds, not what
      // was asked for.
      .catch((e) => {
        grants.reload?.();
        setMemberAction({ severity: 'error', text: e?.response?.data?.detail || 'Could not update approval rights.' });
      })
      .finally(() => setBusyUid(null));
  };

  const createOrg = () => {
    if (!newOrgName.trim()) return;
    setOrgMsg(null);
    iamApi.createOrg(newOrgName.trim())
      .then((o) => { chooseOrg(o.id); setNewOrgName(''); refresh(); setOrgMsg({ severity: 'success', text: `Created "${o.name}".` }); })
      .catch((e) => setOrgMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not create the organization.' }));
  };
  const renameOrg = () => {
    if (!orgName.trim()) return;
    setOrgMsg(null);
    iamApi.renameOrg(activeOrg, orgName.trim())
      .then(() => { refresh(); setOrgMsg({ severity: 'success', text: 'Organization renamed.' }); })
      .catch((e) => setOrgMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not rename the organization.' }));
  };
  // #943: the guard is the in-app modal now, not a native window.confirm — everything
  // after the old early-return moved into `onConfirm`.
  const deleteOrg = () => setConfirm({
    title: 'Delete organization',
    message: `Delete "${activeOrgObj?.name}"? This suspends the organization for every member and cannot be undone from here.`,
    severity: 'warning',
    confirmColor: 'error',
    confirmLabel: 'Delete organization',
    testId: 'delete-org-confirm',
    onConfirm: () => {
      setOrgMsg(null);
      iamApi.deleteOrg(activeOrg)
        .then(() => {
          const remaining = (orgs || []).filter((o) => o.id !== activeOrg);
          chooseOrg(remaining[0]?.id || null);
          refresh();
        })
        .catch((e) => setOrgMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not delete the organization.' }));
    },
  });

  const resetPassword = (m) => {
    // SR-14 (#319): self-service reset only — the admin can no longer set or see
    // a member's password. This just asks Supabase to email them a recovery link.
    setMemberAction(null);
    setBusyUid(m.user_id);
    iamApi.resetMemberPassword(activeOrg, m.user_id)
      .then((r) => setMemberAction({
        severity: r.reset_email_sent ? 'success' : 'warning',
        text: r.reset_email_sent
          ? `Password-reset email sent to ${m.email || m.user_id}.`
          : `Could not send the password-reset email to ${m.email || m.user_id} (their sessions were still revoked).`,
      }))
      .catch((e) => setMemberAction({ severity: 'error', text: e?.response?.data?.detail || 'Could not reset the password.' }))
      .finally(() => setBusyUid(null));
  };
  const generateMagicLink = (m) => {
    setMemberAction(null);
    setBusyUid(m.user_id);
    iamApi.memberMagicLink(activeOrg, m.user_id)
      .then((r) => setMemberAction({
        severity: 'success',
        text: `One-time sign-in link for ${m.email || m.user_id} (shown once — share it securely):`,
        secret: r.link,
      }))
      .catch((e) => setMemberAction({ severity: 'error', text: e?.response?.data?.detail || 'Could not generate a magic link.' }))
      .finally(() => setBusyUid(null));
  };

  if (!activeOrg) return <Alert severity="info">Select or create an organization first.</Alert>;
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Organization setup</Typography>
      <Section title="Organization">
        {orgMsg && <Alert sx={{ mb: 2 }} severity={orgMsg.severity} onClose={() => setOrgMsg(null)}>{orgMsg.text}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap', mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }} useFlexGap>
          {(orgs || []).length > 1 && (
            <Select size="small" value={activeOrg} onChange={(e) => chooseOrg(e.target.value)} sx={{ minWidth: 200 }}>
              {(orgs || []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </Select>
          )}
          <TextField size="small" label="Name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          <Button variant="contained" disabled={!orgName.trim() || orgName.trim() === activeOrgObj?.name} onClick={renameOrg}>
            Rename
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
          <TextField size="small" label="New organization name" value={newOrgName} onChange={(e) => setNewOrgName(e.target.value)} />
          <Button variant="outlined" disabled={!newOrgName.trim()} onClick={createOrg}>Create organization</Button>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Your data</Typography>
        <Button variant="outlined" data-testid="org-export"
          onClick={() => {
            setOrgMsg(null);
            iamApi.exportOrg(activeOrg)
              .then((doc) => {
                const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `rmdv-org-export-${activeOrg}.json`;
                a.click();
                URL.revokeObjectURL(a.href);
                setOrgMsg({ severity: 'success', text: 'Export downloaded (GDPR data portability — everything this organization owns, as JSON).' });
              })
              .catch((e) => setOrgMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not export the organization data.' }));
          }}>Export all data (JSON)</Button>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'error' }}>Danger zone</Typography>
        <Button color="error" variant="outlined" onClick={deleteOrg}>Delete this organization</Button>
      </Section>
      <Tabs
        value={tab}
        sx={{ mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label="Members" {...tabLink(0)} />
        <Tab label="Projects" {...tabLink(1)} />
        <Tab label="Capabilities" {...tabLink(2)} />
        <Tab label="AI keys" {...tabLink(3)} />
        <Tab label="Plan" {...tabLink(4)} />
      </Tabs>
      {tab === 0 && (
        <Section title="Members & invitations">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
            <TextField size="small" label="Email" value={inv.email} onChange={(e) => setInv({ ...inv, email: e.target.value })} />
            <Select size="small" value={inv.role} onChange={(e) => setInv({ ...inv, role: e.target.value })} sx={{ minWidth: 130 }}>
              <MenuItem value="org_admin">Org Admin</MenuItem><MenuItem value="editor">Editor</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem><MenuItem value="member">Member</MenuItem>
            </Select>
            <Button variant="contained" disabled={!inv.email} onClick={() => sendInvite(inv.email, inv.role)}>Invite</Button>
          </Stack>
          {invMsg && (
            <Alert sx={{ mb: 2 }} severity={invMsg.severity}>
              {invMsg.text}
              {invMsg.link && (
                <Box sx={{ mt: 1 }}>
                  <code style={{ wordBreak: 'break-all' }}>{invMsg.link}</code>
                  <Button size="small" sx={{ ml: 1 }} onClick={() => navigator.clipboard?.writeText(invMsg.link)}>Copy link</Button>
                </Box>
              )}
            </Alert>
          )}
          {memberAction && (
            <Alert sx={{ mb: 2 }} severity={memberAction.severity} onClose={() => setMemberAction(null)}>
              {memberAction.text}
              {memberAction.secret && (
                <Box sx={{ mt: 1 }}>
                  <code style={{ wordBreak: 'break-all' }}>{memberAction.secret}</code>
                  <Button size="small" sx={{ ml: 1 }} onClick={() => navigator.clipboard?.writeText(memberAction.secret)}>Copy</Button>
                </Box>
              )}
            </Alert>
          )}
          <Box className="scroll-x"><Table size="small">
            <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>Can approve in</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {members.loading && !members.data && (
                <TableRow><TableCell colSpan={5}><LoadingNote text="Loading members…" /></TableCell></TableRow>
              )}
              {(members.data || []).map((m) => (
                <TableRow key={m.user_id}>
                  <TableCell>{m.display_name || m.email || m.user_id}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={m.role || 'member'}
                      disabled={busyUid === m.user_id}
                      onChange={(e) => assignRole(m.user_id, e.target.value)}
                      slotProps={{ htmlInput: { 'aria-label': `Role for ${m.email || m.user_id}` } }}
                      data-testid={`org-role-select-${m.user_id}`}
                      sx={{ minWidth: 130 }}
                    >
                      <MenuItem value="org_admin">Org Admin</MenuItem>
                      <MenuItem value="editor">Editor</MenuItem>
                      <MenuItem value="viewer">Viewer</MenuItem>
                      <MenuItem value="member">Member</MenuItem>
                    </Select>
                  </TableCell>
                  {/* N-11 / REQ-6130 (#601): where this member may SIGN OFF. Multi-select
                      because grants are additive and scoped — "org-wide" plus any number
                      of named projects. Empty = no approval rights, which since #591 is
                      a real state: `edit` no longer implies sign-off. `org_admin` holds
                      approval through its wildcard, so its row reads "via Org Admin"
                      rather than offering a grant that would change nothing. */}
                  <TableCell>
                    {m.role === 'org_admin' ? (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}
                                  data-testid={`approver-scope-admin-${m.user_id}`}>
                        All projects (via Org Admin)
                      </Typography>
                    ) : (
                      <Select
                        multiple
                        size="small"
                        value={approverScopesFor(m.user_id)}
                        disabled={busyUid === m.user_id}
                        onChange={(e) => setApproverScopes(m.user_id,
                          typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        displayEmpty
                        renderValue={(sel) => (sel.length === 0 ? 'No approval rights' : sel.map(
                          (s) => (s === ORG_WIDE ? 'Org-wide'
                            : ((projects.data || []).find((p) => p.id === s)?.name || s)),
                        ).join(', '))}
                        slotProps={{ htmlInput: { 'aria-label': `Approval scopes for ${m.email || m.user_id}` } }}
                        data-testid={`approver-scope-select-${m.user_id}`}
                        sx={{ minWidth: 190 }}
                      >
                        <MenuItem value={ORG_WIDE}>Org-wide (every project)</MenuItem>
                        {(projects.data || []).map((p) => (
                          <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                        ))}
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>{m.status || '—'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" disabled={busyUid === m.user_id} onClick={() => resetPassword(m)}>Reset password</Button>
                    {/* SR-14 (#319): minting a member sign-in link is super-admin-only
                        (an org-admin doing it is user-switching). Org-admins keep the
                        self-service "Reset password" (emails the member a recovery link). */}
                    {isSuperAdmin && (
                      <Button size="small" disabled={busyUid === m.user_id} onClick={() => generateMagicLink(m)}>Magic link</Button>
                    )}
                    <Button size="small" disabled={busyUid === m.user_id}
                      onClick={() => setConfirm({
                        title: 'Sign out everywhere',
                        message: `Sign ${m.email || m.user_id} out everywhere? Their existing sessions stop working; they can sign in again.`,
                        confirmColor: 'error',
                        confirmLabel: 'Sign out everywhere',
                        testId: 'revoke-member-sessions-confirm',
                        onConfirm: () => {
                          setMemberAction(null);
                          setBusyUid(m.user_id);
                          iamApi.revokeMemberSessions(activeOrg, m.user_id)
                            .then(() => setMemberAction({ severity: 'success', text: `${m.email || m.user_id} has been signed out everywhere.` }))
                            .catch((e) => setMemberAction({ severity: 'error', text: e?.response?.data?.detail || 'Could not revoke the sessions.' }))
                            .finally(() => setBusyUid(null));
                        },
                      })}>Sign out everywhere</Button>
                    <Button size="small" color="error" disabled={busyUid === m.user_id}
                      onClick={() => setConfirm({
                        title: 'Remove member',
                        message: `Remove ${m.email || m.user_id} from this organization?`,
                        confirmColor: 'error',
                        confirmLabel: 'Remove',
                        testId: 'remove-org-member-confirm',
                        onConfirm: () => {
                          setMemberAction(null);
                          setBusyUid(m.user_id);
                          iamApi.removeOrgMember(activeOrg, m.user_id)
                            .then(() => members.reload?.())
                            .catch((e) => setMemberAction({ severity: 'error', text: e?.response?.data?.detail || 'Could not remove the member.' }))
                            .finally(() => setBusyUid(null));
                        },
                      })}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table></Box>
          {(invitations.data || []).length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Pending invitations</Typography>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Invited but haven&apos;t signed in yet — they show here, not above, until their first
                login (that&apos;s what actually joins them to the org). Resend to get a fresh sign-in
                link, e.g. if the first one expired or wasn&apos;t reachable.
              </Typography>
              <Box className="scroll-x"><Table size="small">
                <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Role</TableCell><TableCell>Expires</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                <TableBody>
                  {(invitations.data || []).map((iv) => (
                    <TableRow key={iv.id}>
                      <TableCell>{iv.email}</TableCell>
                      <TableCell>{iv.role}</TableCell>
                      <TableCell>{iv.expires_at ? new Date(iv.expires_at).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">
                        <Button size="small" onClick={() => sendInvite(iv.email, iv.role)}>Resend</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></Box>
            </>
          )}
        </Section>
      )}
      {tab === 1 && (
        <Section title="Projects & access">
          <Typography variant="body2" sx={{ mb: 1 }}>
            Projects partition this org&apos;s requirements. Create a project, then open it to grant
            members access and set their role (admin / editor / viewer). Each user chooses which of
            their projects to show in the requirements tree under Settings → Display.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
            <TextField size="small" label="New project name" value={newProj} onChange={(e) => setNewProj(e.target.value)} />
            <Button variant="contained" disabled={!newProj.trim()}
              onClick={() => {
                setProjMsg(null);
                iamApi.createProject(activeOrg, newProj.trim())
                  .then(() => { setNewProj(''); projects.reload?.(); setProjMsg({ severity: 'success', text: 'Project created.' }); })
                  .catch((e) => setProjMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not create the project.' }));
              }}>Create project</Button>
          </Stack>
          {projMsg && <Alert sx={{ mb: 2 }} severity={projMsg.severity}>{projMsg.text}</Alert>}
          <Box className="scroll-x"><Table size="small">
            <TableHead><TableRow><TableCell>Project</TableCell><TableCell>Your role</TableCell><TableCell align="right">Manage</TableCell></TableRow></TableHead>
            <TableBody>
              {projects.loading && !projects.data && (
                <TableRow><TableCell colSpan={3}><LoadingNote text="Loading projects…" /></TableCell></TableRow>
              )}
              {(projects.data || []).map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.name || p.title || p.id}</TableCell>
                  <TableCell>{p.role || '—'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => navigate(`/account/projects/${p.id}/config`)}>Configure</Button>
                    <Button size="small" onClick={() => setCopyTarget(p)}>Copy</Button>
                    <Button size="small" color="error" onClick={() => setArchiveTarget(p)}>Archive</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!projects.loading && (projects.data || []).length === 0 && (
                <TableRow><TableCell colSpan={3}><Typography variant="body2" sx={{ color: 'text.secondary' }}>No projects yet — create one above.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table></Box>
          <ProjectDangerDialog
            open={!!archiveTarget}
            variant="archive"
            projectName={archiveTarget?.name || archiveTarget?.title || archiveTarget?.id}
            onClose={() => setArchiveTarget(null)}
            onConfirm={() => {
              const target = archiveTarget;
              setArchiveTarget(null);
              setProjMsg(null);
              iamApi.archiveProject(target.id)
                .then(() => { projects.reload?.(); setProjMsg({ severity: 'success', text: 'Project archived.' }); })
                .catch((e) => setProjMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not archive the project.' }));
            }}
          />
          <CopyProjectDialog
            open={!!copyTarget}
            project={copyTarget}
            onClose={() => setCopyTarget(null)}
            onCopy={(name, mode) => {
              const target = copyTarget;
              return iamApi.copyProject(target.id, { name, mode }).then((r) => {
                setCopyTarget(null);
                projects.reload?.();
                const extra = mode === 'full'
                  ? ` (${r.copied?.items ?? 0} item(s), ${r.copied?.links ?? 0} link(s) copied)`
                  : '';
                setProjMsg({ severity: 'success', text: `Copied "${target.name}" to "${r.name}"${extra}.` });
              });
            }}
          />
        </Section>
      )}
      {tab === 2 && (
        <Section title="Capabilities">
          <Typography variant="body2" sx={{ mb: 1 }}>You can enable capabilities the platform has made available to your org.</Typography>
          <List>
            {caps.loading && !caps.data && (
              <ListItem><LoadingNote text="Loading capabilities…" /></ListItem>
            )}
            {(caps.data || []).map((c) => (
              <ListItem key={c.capability_key}>
                <ListItemText primary={c.label || c.capability_key}
                  secondary={c.allowed ? 'Available on your plan — toggle to enable for this organization.'
                    : 'Not available on your plan. Contact the platform administrator to enable it.'} />
                <Toggle label={`Enable ${c.label || c.capability_key} for this organization`}
                  on={!!c.enabled} disabled={!c.allowed}
                  onChange={(v) => iamApi.setOrgCap(activeOrg, c.capability_key, v).then(caps.reload)} />
              </ListItem>
            ))}
            {!caps.loading && (caps.data || []).length === 0 && <Typography variant="body2">No capabilities configured.</Typography>}
          </List>
        </Section>
      )}
      {tab === 3 && (
        <Section title="Organization LLM keys">
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            Your organization&apos;s own LLM credential. When set for the active provider, every
            member&apos;s AI requests run on it (a member&apos;s personal key still takes precedence);
            without one, the platform&apos;s default key is used. One key per provider — saving again
            replaces it. Keys are stored encrypted and never shown again; changes are audited.
          </Typography>
          {opkMsg && <Alert sx={{ mb: 2 }} severity={opkMsg.severity} onClose={() => setOpkMsg(null)}>{opkMsg.text}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap', mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }} useFlexGap>
            <Select size="small" value={opk.provider} onChange={(e) => setOpk({ ...opk, provider: e.target.value })} data-testid="org-key-provider">
              {LLM_PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
            <TextField size="small" label="API key" type="password" value={opk.key}
              onChange={(e) => setOpk({ ...opk, key: e.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'org-key-input' } }} />
            <TextField size="small" label="Endpoint override (optional)" value={opk.endpoint_override}
              onChange={(e) => setOpk({ ...opk, endpoint_override: e.target.value })} />
            <Button variant="contained" disabled={!opk.key.trim()} data-testid="org-key-save"
              onClick={() => {
                setOpkMsg(null);
                iamApi.setOrgProviderKey(activeOrg, { provider: opk.provider, key: opk.key.trim(), endpoint_override: opk.endpoint_override.trim() || null })
                  .then(() => { setOpk({ ...opk, key: '' }); setOpkMsg({ severity: 'success', text: 'Organization key saved.' }); orgKeys.reload?.(); })
                  .catch((e) => setOpkMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not save the key.' }));
              }}>Save key</Button>
          </Stack>
          <Box className="scroll-x"><Table size="small" data-testid="org-keys-table">
            <TableHead><TableRow><TableCell>Provider</TableCell><TableCell>Endpoint</TableCell><TableCell>Added</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
            <TableBody>
              {orgKeys.loading && !orgKeys.data && (
                <TableRow><TableCell colSpan={4}><LoadingNote text="Loading organization keys…" /></TableCell></TableRow>
              )}
              {(orgKeys.data || []).map((k) => (
                <TableRow key={k.id}>
                  <TableCell>{k.provider}</TableCell>
                  <TableCell>{k.endpoint_override || 'default'}</TableCell>
                  <TableCell>{k.created_at ? new Date(k.created_at).toLocaleString() : '—'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" color="error"
                      onClick={() => iamApi.delOrgProviderKey(activeOrg, k.id)
                        .then(() => { setOpkMsg({ severity: 'success', text: 'Key removed.' }); orgKeys.reload?.(); })
                        .catch((e) => setOpkMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not remove the key.' }))}>Remove</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!orgKeys.loading && (orgKeys.data || []).length === 0 && (
                <TableRow><TableCell colSpan={4}><Typography variant="body2" sx={{ color: 'text.secondary' }}>No organization keys — members use the platform default.</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table></Box>
        </Section>
      )}
      {tab === 4 && <Section title="Plan & usage"><OrgPlanTab /></Section>}

      {/* #943: in-app confirmation modal, replacing the native window.confirm the
          org-delete, session-revoke and member-remove actions used. */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.onConfirm(); }}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        severity={confirm?.severity || null}
        confirmColor={confirm?.confirmColor || 'primary'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        testId={confirm?.testId || 'org-settings-confirm-dialog'}
      />
    </Box>
  );
}

// --- Project config --------------------------------------------------------
const PROJECT_ROLES = ['project_admin', 'editor', 'viewer'];
const FIELD_DATA_TYPES = ['text', 'textarea', 'number', 'enum', 'multiselect', 'date', 'boolean', 'reference', 'svg_image'];
const OPTIONS_TYPES = new Set(['enum', 'multiselect']);
// `show_in_mainTouched` (#197): tracks whether the admin EXPLICITLY clicked the
// "Show on Item Detail main area" checkbox for a NEW field. When they never touch
// it, the Add-field submit omits `show_in_main` entirely (rather than always
// sending an explicit `false`) so the backend's own default applies — a required
// field defaults to visible without anyone having to remember to tick the box.
const EMPTY_FIELD_FORM = { key: '', label: '', data_type: 'text', required: false, options: '',
                           show_in_main: false, show_in_mainTouched: false };

// One editable field row inside an item type's field list: view mode shows
// label/type/flags with edit+delete icons; edit mode swaps in inline controls.
function FieldRow({ field, onSave, onDelete, readOnly }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const startEdit = () => {
    setDraft({
      label: field.label,
      data_type: field.data_type,
      required: !!field.required,
      options: (field.options || []).join(', '),
      show_in_main: !!field.show_in_main,
    });
    setEditing(true);
  };

  const save = () => {
    onSave({
      label: draft.label,
      data_type: draft.data_type,
      required: draft.required,
      options: OPTIONS_TYPES.has(draft.data_type) ? draft.options.split(',').map((s) => s.trim()).filter(Boolean) : [],
      show_in_main: draft.show_in_main,
    });
    setEditing(false);
  };

  if (!editing) {
    return (
      <TableRow>
        <TableCell>{field.label} <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>({field.key})</Typography></TableCell>
        <TableCell>{field.data_type}</TableCell>
        <TableCell>{field.required ? 'Yes' : 'No'}</TableCell>
        <TableCell>{(field.options || []).join(', ')}</TableCell>
        <TableCell>{field.show_in_main ? 'Yes' : 'No'}</TableCell>
        <TableCell align="right">
          {/* #197 follow-up: the backend 403s system-type field edits for anyone
              but a super-admin — don't offer pencils that only end in an error. */}
          {!readOnly && (
            <>
              <IconButton size="small" aria-label={`Edit ${field.label}`} onClick={startEdit}><EditIcon fontSize="small" /></IconButton>
              <IconButton size="small" aria-label={`Delete ${field.label}`} onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton>
            </>
          )}
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow>
      <TableCell><TextField size="small" value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} /></TableCell>
      <TableCell>
        <Select size="small" value={draft.data_type} onChange={(e) => setDraft({ ...draft, data_type: e.target.value })}>
          {FIELD_DATA_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </Select>
      </TableCell>
      <TableCell>
        <Checkbox size="small" checked={draft.required} onChange={(e) => setDraft({ ...draft, required: e.target.checked })} />
      </TableCell>
      <TableCell>
        {OPTIONS_TYPES.has(draft.data_type) && (
          <TextField size="small" placeholder="opt1, opt2" value={draft.options} onChange={(e) => setDraft({ ...draft, options: e.target.value })} />
        )}
      </TableCell>
      <TableCell>
        <Checkbox size="small" checked={draft.show_in_main} onChange={(e) => setDraft({ ...draft, show_in_main: e.target.checked })} />
      </TableCell>
      <TableCell align="right">
        <IconButton size="small" aria-label={`Save ${field.label}`} onClick={save}><CheckIcon fontSize="small" /></IconButton>
        <IconButton size="small" aria-label={`Cancel editing ${field.label}`} onClick={() => setEditing(false)}><CloseIcon fontSize="small" /></IconButton>
      </TableCell>
    </TableRow>
  );
}

// One item type card: label (inline-editable unless system), its fields table
// with edit/delete, and an "add field" inline form.
function ItemTypeCard({ type: t, checked, canToggleAttach, canManage, onToggleAttach, onRename, onDelete, onAddField, onSaveField, onDeleteField, onBulkSetShowInMain, onRestamp }) {
  const [renaming, setRenaming] = useState(false);
  const [label, setLabel] = useState(t.label);
  const [prefix, setPrefix] = useState(t.prefix || '');
  const [showAddField, setShowAddField] = useState(false);
  const [fieldForm, setFieldForm] = useState(EMPTY_FIELD_FORM);
  // #793: `renaming` flips this inline editor on/off; a bare `autoFocus`
  // cannot express "focus every time this becomes active". See
  // hooks/useAutoFocus.
  const focusRef = useAutoFocus(renaming);
  // #197/#1037: `canManage` is resolved by the caller (ProjectConfig) — system
  // types are super-admin-only, non-system types need org-wide manage_project OR
  // a project admin whose own project is the type's ONLY attachment. Add-field,
  // bulk-toggle, edit/delete pencils and rename all key off the same flag so a
  // control that would 403 is never offered in the first place (#1042).
  const readOnly = !canManage;

  const submitRename = () => {
    const trimmedLabel = label.trim();
    const trimmedPrefix = prefix.trim().toUpperCase();
    const labelChanged = trimmedLabel && trimmedLabel !== t.label;
    const prefixChanged = trimmedPrefix !== (t.prefix || '');
    if (labelChanged || prefixChanged) {
      // Only pass a prefix arg at all when it actually changed — a plain label
      // rename must look exactly like it always has (#174 part 2).
      if (prefixChanged) onRename(trimmedLabel || t.label, trimmedPrefix);
      else onRename(trimmedLabel || t.label);
    }
    setRenaming(false);
  };

  const submitAddField = () => {
    if (!fieldForm.key.trim() || !fieldForm.label.trim()) return;
    onAddField({
      key: fieldForm.key.trim(),
      label: fieldForm.label.trim(),
      data_type: fieldForm.data_type,
      required: fieldForm.required,
      options: OPTIONS_TYPES.has(fieldForm.data_type) ? fieldForm.options.split(',').map((s) => s.trim()).filter(Boolean) : [],
      // Omit entirely (undefined, dropped by JSON.stringify) unless the admin
      // actually clicked the checkbox — lets the backend's own required-field
      // default apply instead of this form always forcing an explicit `false`.
      show_in_main: fieldForm.show_in_mainTouched ? fieldForm.show_in_main : undefined,
    });
    setFieldForm(EMPTY_FIELD_FORM);
    setShowAddField(false);
  };

  return (
    <Box sx={{ mb: 3 }} data-testid="item-type-card">
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
        <FormControlLabel
          control={(
            <Checkbox checked={checked} disabled={!canToggleAttach}
              onChange={(e) => onToggleAttach(e.target.checked)} />
          )}
          label=""
          sx={{ mr: 0 }}
          title={canToggleAttach ? undefined : 'Only an org admin can attach or detach item types.'}
        />
        {renaming ? (
          <>
            <TextField size="small" label="Label" value={label} onChange={(e) => setLabel(e.target.value)} inputRef={focusRef} />
            <TextField size="small" label="Prefix" value={prefix} sx={{ width: 120 }}
              onChange={(e) => setPrefix(e.target.value)} />
            <IconButton size="small" aria-label="Save type name" onClick={submitRename}><CheckIcon fontSize="small" /></IconButton>
            <IconButton size="small" aria-label="Cancel rename" onClick={() => { setLabel(t.label); setPrefix(t.prefix || ''); setRenaming(false); }}><CloseIcon fontSize="small" /></IconButton>
          </>
        ) : (
          <>
            <Typography variant="subtitle1">{t.label}</Typography>
            <Chip size="small" label={t.key} />
            {t.prefix && <Chip size="small" color="primary" variant="outlined" label={`${t.prefix}-NNNN`} />}
            {t.is_system ? (
              <Chip size="small" color="default" variant="outlined" label="system" />
            ) : canManage ? (
              <>
                <IconButton size="small" aria-label={`Rename ${t.label}`} onClick={() => setRenaming(true)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" aria-label={`Delete ${t.label}`} onClick={onDelete}><DeleteIcon fontSize="small" /></IconButton>
              </>
            ) : null}
          </>
        )}
      </Stack>
      {!readOnly && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1 }} useFlexGap>
          <Button size="small" data-testid="bulk-show-in-main" disabled={(t.fields || []).length === 0}
            onClick={() => onBulkSetShowInMain(true)}>
            Show all in main area
          </Button>
          <Button size="small" data-testid="bulk-hide-in-main" disabled={(t.fields || []).length === 0}
            onClick={() => onBulkSetShowInMain(false)}>
            Hide all from main area
          </Button>
          {/* #174 residual gap: display_id is stamped once at creation time from the
              type's prefix at that moment — this re-stamps EXISTING items under the
              CURRENT prefix. Only offered once a prefix is actually configured. */}
          {t.prefix && (
            <Button size="small" data-testid="restamp-items" onClick={onRestamp}>
              Re-stamp existing items with {t.prefix}
            </Button>
          )}
        </Stack>
      )}
      <Box className="scroll-x"><Table size="small">
        <TableHead><TableRow>
          <TableCell>Label</TableCell><TableCell>Type</TableCell><TableCell>Required</TableCell>
          <TableCell>Options</TableCell><TableCell>Show on Item Detail main area</TableCell><TableCell align="right">Actions</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {(t.fields || []).map((f) => (
            <FieldRow key={f.id} field={f} onSave={(patch) => onSaveField(f.id, patch)} onDelete={() => onDeleteField(f.id)} readOnly={readOnly} />
          ))}
        </TableBody>
      </Table></Box>
      {readOnly ? null : showAddField ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap', mt: 1, alignItems: { xs: 'stretch', sm: 'flex-end' } }} useFlexGap>
          <TextField size="small" label="Key" value={fieldForm.key} onChange={(e) => setFieldForm({ ...fieldForm, key: e.target.value })} />
          <TextField size="small" label="Label" value={fieldForm.label} onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })} />
          <Select size="small" value={fieldForm.data_type} onChange={(e) => setFieldForm({ ...fieldForm, data_type: e.target.value })}>
            {FIELD_DATA_TYPES.map((dt) => <MenuItem key={dt} value={dt}>{dt}</MenuItem>)}
          </Select>
          <FormControlLabel control={<Checkbox checked={fieldForm.required} onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })} />} label="Required" />
          {OPTIONS_TYPES.has(fieldForm.data_type) && (
            <TextField size="small" label="Options (comma-separated)" value={fieldForm.options} onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })} />
          )}
          <FormControlLabel control={<Checkbox checked={fieldForm.show_in_main} onChange={(e) => setFieldForm({ ...fieldForm, show_in_main: e.target.checked, show_in_mainTouched: true })} />} label="Show on Item Detail main area" />
          <Button variant="contained" disabled={!fieldForm.key.trim() || !fieldForm.label.trim()} onClick={submitAddField}>Add field</Button>
          <Button onClick={() => { setShowAddField(false); setFieldForm(EMPTY_FIELD_FORM); }}>Cancel</Button>
        </Stack>
      ) : (
        <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => setShowAddField(true)}>Add field</Button>
      )}
    </Box>
  );
}

export function ProjectConfig() {
  const { pid } = useParams();
  const { activeOrg, isSuperAdmin, orgs, user } = useAuth();
  const navigate = useNavigate();
  const project = useAsync(() => iamApi.getProject(pid), [pid]);
  const types = useAsync(() => iamApi.projectItemTypes(pid), [pid]);
  const orgTypes = useAsync(() => (activeOrg ? iamApi.orgItemTypes(activeOrg) : Promise.resolve([])), [activeOrg]);
  const members = useAsync(() => iamApi.projectMembers(pid), [pid]);
  const orgMembers = useAsync(() => (activeOrg ? iamApi.orgMembers(activeOrg) : Promise.resolve([])), [activeOrg]);
  // #1037: org-wide capability (as today, unchanged) vs a plain PROJECT admin, who may
  // only manage a non-system type attached to exactly THIS project (backend a60bfb5).
  // `orgs` carries the caller's ORG-level role from /iam/me; org_admin/super_admin already
  // held every one of these controls and must keep them exactly as they are.
  const orgRole = (orgs || []).find((o) => o.id === activeOrg)?.role;
  const canManageOrgWide = isSuperAdmin || orgRole === 'org_admin';
  const isProjectAdmin = !canManageOrgWide
    && (members.data || []).some((m) => m.user_id === user?.id && m.role === 'project_admin');
  // #1042: both /projects/{pid}/item-types and /orgs/{org_id}/item-types now carry
  // each type's `project_count` (backend, this change) — the exact number of projects
  // it's attached to, computed server-side over the full project_item_types table, not
  // just the ones this caller can see. That replaces the client-side approximation this
  // page used to build by fetching iamApi.myProjects and then every OTHER visible
  // project's item-types (one request per project, on every render, and still wrong for
  // a project the admin can't see). See the eligibility check below.
  const [add, setAdd] = useState({ uid: '', role: 'editor' });
  const [msg, setMsg] = useState(null);
  const [newType, setNewType] = useState({ key: '', label: '', prefix: '' });
  const [projName, setProjName] = useState('');
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showHardDeleteDialog, setShowHardDeleteDialog] = useState(false);
  // #388: re-stamp confirm + progress dialog state. `phase` walks
  // confirm → running → done|error; `done`/`total` drive the progress bar.
  const [restamp, setRestamp] = useState(null);
  // In-app confirmation modal (replaces window.confirm, #943). Holds the descriptor
  // for the pending action — { title, message, severity, confirmLabel, confirmColor,
  // testId, onConfirm } — or null when nothing is awaiting confirmation.
  const [confirm, setConfirm] = useState(null);

  useEffect(() => { setProjName(project.data?.name || ''); }, [project.data?.name]);

  const assign = (uid, role) => {
    setMsg(null);
    iamApi.assignProjectRole(pid, uid, role)
      .then(() => { members.reload?.(); setMsg({ severity: 'success', text: `${uid} is now ${role} on this project.` }); })
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not assign the project role.' }));
  };

  // #943: the guard is the in-app modal now, not a native window.confirm — everything
  // after the old early-return moved into `onConfirm`.
  const removeMember = (uid) => setConfirm({
    title: 'Remove member',
    message: 'Remove this member from the project?',
    confirmColor: 'error',
    confirmLabel: 'Remove',
    testId: 'remove-project-member-confirm',
    onConfirm: () => {
      setMsg(null);
      iamApi.removeProjectMember(pid, uid)
        .then(() => members.reload?.())
        .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not remove the member.' }));
    },
  });

  const renameProject = () => {
    if (!projName.trim()) return;
    setMsg(null);
    iamApi.renameProject(pid, projName.trim())
      .then(() => { project.reload?.(); setMsg({ severity: 'success', text: 'Project renamed.' }); })
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not rename the project.' }));
  };

  const confirmArchiveProject = () => {
    setShowArchiveDialog(false);
    iamApi.archiveProject(pid)
      .then(() => navigate('/account/org'))
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not archive the project.' }));
  };

  // Super-admin-only, irreversible hard delete (#195) — distinct from the archive
  // above, which merely hides the project and keeps its data.
  const confirmHardDeleteProject = () => {
    setShowHardDeleteDialog(false);
    iamApi.hardDeleteProject(pid)
      .then(() => navigate('/account/org'))
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not permanently delete the project.' }));
  };

  // Org members not yet on the project — candidates to grant access.
  const current = new Set((members.data || []).map((m) => m.user_id));
  const candidates = (orgMembers.data || []).filter((m) => !current.has(m.user_id));

  // --- item type / meta-model helpers --------------------------------------
  const attachedIds = new Set((types.data || []).map((t) => t.id));

  const reloadTypes = () => { types.reload?.(); orgTypes.reload?.(); };

  const setTypes = (ids) => iamApi.setProjectItemTypes(pid, ids).then(reloadTypes)
    .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not update item types for this project.' }));

  const toggleAttach = (typeId, on) => {
    setMsg(null);
    const ids = new Set(attachedIds);
    if (on) ids.add(typeId); else ids.delete(typeId);
    setTypes(Array.from(ids));
  };

  const createType = () => {
    if (!newType.key.trim() || !newType.label.trim()) return;
    const kerr = keyError(newType.key);
    if (kerr) { setMsg({ severity: 'error', text: kerr }); return; }
    setMsg(null);
    const body = {
      key: newType.key.trim().toLowerCase(), label: newType.label.trim(), prefix: newType.prefix.trim() || null,
    };
    // #1037: a plain project admin holds no org-wide manage_project grant, so the
    // create-then-PUT-the-whole-set flow below (still used by org/super admins,
    // unchanged) would 403 on the PUT. Sending project_id instead authorises against
    // THIS project's own grant and auto-attaches only the new type — additive, leaves
    // every other attached type alone.
    if (!canManageOrgWide) body.project_id = pid;
    iamApi.createItemType(activeOrg, body)
      .then((r) => (canManageOrgWide ? setTypes(Array.from(new Set([...attachedIds, r.id]))) : reloadTypes()))
      .then(() => { setNewType({ key: '', label: '', prefix: '' }); setMsg({ severity: 'success', text: `Created item type "${newType.label}".` }); })
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not create item type.' }));
  };

  // `prefix` is only passed through to the API when the caller actually
  // supplied one — plain label renames must not silently clear an existing
  // prefix (#174 part 2).
  const renameType = (typeId, label, prefix) => {
    setMsg(null);
    const req = prefix === undefined
      ? iamApi.renameItemType(typeId, label)
      : iamApi.renameItemType(typeId, label, prefix);
    req.then(reloadTypes)
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not rename item type.' }));
  };

  const deleteType = (typeId, typeLabel) => setConfirm({
    title: 'Delete item type',
    message: `Delete item type "${typeLabel}"? This cannot be undone.`,
    severity: 'warning',
    confirmColor: 'error',
    confirmLabel: 'Delete item type',
    testId: 'delete-item-type-confirm',
    onConfirm: () => {
      setMsg(null);
      iamApi.deleteItemType(typeId).then(reloadTypes)
        .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not delete item type.' }));
    },
  });

  const addField = (typeId, body) => {
    const kerr = keyError(body && body.key);
    if (kerr) { setMsg({ severity: 'error', text: kerr }); return; }
    setMsg(null);
    iamApi.addField(typeId, { ...body, key: body.key.trim().toLowerCase() }).then(reloadTypes)
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not add field.' }));
  };

  const saveField = (typeId, fieldId, patch) => {
    setMsg(null);
    iamApi.updateField(typeId, fieldId, patch).then(reloadTypes)
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not update field.' }));
  };

  const deleteField = (typeId, fieldId) => setConfirm({
    title: 'Delete field',
    message: 'Delete this field? This cannot be undone.',
    severity: 'warning',
    confirmColor: 'error',
    confirmLabel: 'Delete field',
    testId: 'delete-field-confirm',
    onConfirm: () => {
      setMsg(null);
      iamApi.deleteField(typeId, fieldId).then(reloadTypes)
        .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not delete field.' }));
    },
  });

  // Bulk-toggle `show_in_main` across every field of a type in one action
  // (#197) — reuses the existing per-field updateField endpoint rather than
  // adding a new batch endpoint.
  const bulkSetShowInMain = (typeId, fields, showInMain) => {
    setMsg(null);
    Promise.all((fields || []).map((f) => iamApi.updateField(typeId, f.id, { show_in_main: showInMain })))
      .then(reloadTypes)
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not update fields.' }));
  };

  // Prefix re-stamp (#174 residual gap / #388): show the affected count BEFORE the
  // admin confirms, then re-stamp via the shared id-sequence machinery, in batches
  // so a large type shows a real progress bar (each item also gets a History entry).
  const RESTAMP_BATCH = 100;
  const restampType = (typeId, typeLabel, prefix) => {
    setMsg(null);
    iamApi.restampPreview(typeId).then((preview) => {
      if (!preview.count) {
        setMsg({ severity: 'info', text: `No existing items of type "${typeLabel}" to re-stamp.` });
        return;
      }
      setRestamp({ typeId, typeLabel, prefix, phase: 'confirm', mode: 'keep_number',
        total: preview.count, done: 0, restamped: 0 });
    }).catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not preview the re-stamp.' }));
  };

  // Drive the batched re-stamp, advancing the progress bar after each batch.
  const runRestamp = async () => {
    setRestamp((s) => ({ ...s, phase: 'running' }));
    const { typeId, mode } = restamp;
    try {
      let done = 0;
      let restamped = 0;
      let total = restamp.total;
      // Loop until the server reports the whole type is covered. `done` is the
      // server's cursor, so this terminates even if items were added/removed.
      while (true) {
         
        const r = await iamApi.restampItemType(typeId, { offset: done, limit: RESTAMP_BATCH, mode });
        done = r.done;
        total = r.total;
        restamped += r.restamped;
        setRestamp((s) => (s ? { ...s, done, total, restamped } : s));
        if (r.complete) break;
      }
      reloadTypes();
      setRestamp((s) => (s ? { ...s, phase: 'done', done, total, restamped } : s));
    } catch (e) {
      setRestamp((s) => (s ? { ...s, phase: 'error',
        error: e?.response?.data?.detail || 'Could not re-stamp items.' } : s));
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Project configuration</Typography>
      <Section title="Project settings">
        {msg && <Alert sx={{ mb: 2 }} severity={msg.severity}>{msg.text}</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
          <TextField size="small" label="Name" value={projName} onChange={(e) => setProjName(e.target.value)} />
          <Button size="small" variant="contained" disabled={!projName.trim() || projName.trim() === project.data?.name} onClick={renameProject}>
            Rename
          </Button>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'error' }}>Danger zone</Typography>
        <Button size="small" color="error" variant="outlined" onClick={() => setShowArchiveDialog(true)}>
          Archive this project
        </Button>
        {isSuperAdmin && (
          <Button size="small" color="error" variant="contained" sx={{ ml: 1 }}
            onClick={() => setShowHardDeleteDialog(true)}>
            Permanently delete (super admin)
          </Button>
        )}
        <ProjectDangerDialog
          open={showArchiveDialog}
          variant="archive"
          projectName={project.data?.name}
          onClose={() => setShowArchiveDialog(false)}
          onConfirm={confirmArchiveProject}
        />
        {isSuperAdmin && (
          <ProjectDangerDialog
            open={showHardDeleteDialog}
            variant="hardDelete"
            projectName={project.data?.name}
            onClose={() => setShowHardDeleteDialog(false)}
            onConfirm={confirmHardDeleteProject}
          />
        )}
      </Section>
      <Section title="Members & access">
        <Typography variant="body2" sx={{ mb: 1 }}>
          Grant org members access to this project and set their role. Project roles override
          the org role for items in this project (a &apos;member&apos; gains access via a project role).
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }}>
          <Select size="small" displayEmpty value={add.uid} onChange={(e) => setAdd({ ...add, uid: e.target.value })} sx={{ minWidth: 220 }}>
            {/* #886: `candidates` is empty while orgMembers is still in flight, so the
                picker read "there is nobody left to add" during every load. */}
            <MenuItem value="" disabled>
              {orgMembers.loading && !orgMembers.data ? 'Loading org members…' : 'Select an org member…'}
            </MenuItem>
            {candidates.map((m) => <MenuItem key={m.user_id} value={m.user_id}>{m.email || m.user_id}</MenuItem>)}
          </Select>
          <Select size="small" value={add.role} onChange={(e) => setAdd({ ...add, role: e.target.value })} sx={{ minWidth: 130 }}>
            {PROJECT_ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
          <Button variant="contained" disabled={!add.uid} onClick={() => { assign(add.uid, add.role); setAdd({ uid: '', role: 'editor' }); }}>
            Grant access
          </Button>
        </Stack>
        <Box className="scroll-x"><Table size="small">
          <TableHead><TableRow><TableCell>User</TableCell><TableCell>Role</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          {/* #886: ProjectConfig was left out of #818's loading gates, so an empty
              members table rendered for the whole fetch and read as "no members"
              rather than "still loading". Same LoadingNote the org-settings and
              UserSettings tabs already use. */}
          <TableBody>{members.loading && !members.data ? (
            <TableRow><TableCell colSpan={3}><LoadingNote text="Loading project members…" /></TableCell></TableRow>
          ) : (members.data || []).map((m) => (
            <TableRow key={m.user_id}>
              <TableCell>{m.display_name || m.email || m.user_id}</TableCell>
              <TableCell>
                <Select size="small" value={PROJECT_ROLES.includes(m.role) ? m.role : ''} displayEmpty
                  onChange={(e) => assign(m.user_id, e.target.value)}>
                  {!PROJECT_ROLES.includes(m.role) && <MenuItem value="" disabled>{m.role || '—'}</MenuItem>}
                  {PROJECT_ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </TableCell>
              <TableCell align="right">
                <Button size="small" color="error" onClick={() => removeMember(m.user_id)}>Remove</Button>
              </TableCell>
            </TableRow>
          ))}</TableBody>
        </Table></Box>
      </Section>
      <Section title="Item types & fields (meta-model)">
        <Typography variant="body2" sx={{ mb: 2 }}>
          Check a type to attach it to this project (uncheck to detach). Create new org-wide
          types here, then attach the ones this project needs. System types are shared platform
          defaults and can&apos;t be renamed or deleted.
        </Typography>
        {msg && <Alert sx={{ mb: 2 }} severity={msg.severity}>{msg.text}</Alert>}
        {/* #1037: a plain project admin gets this form too — createType() passes
            project_id and the backend authorises + auto-attaches. Hidden for anyone
            with neither an org-wide nor a project-scoped manage_project grant, so the
            button is never offered to someone who'd just get a 403 (#1042). */}
        {(canManageOrgWide || isProjectAdmin) && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap', mb: 3, alignItems: { xs: 'stretch', sm: 'flex-end' } }} useFlexGap>
            {/* #1035: Key/Label/Prefix used to share no common helper-text slot, so the
                Prefix field (the only one with helperText) stood taller than the other
                two and `alignItems: flex-end` pinned everything to ITS bottom edge —
                Prefix's own input sat visibly higher than Key/Label, and the button
                floated free of all three. The hint now lives in Prefix's placeholder
                (no extra height), and each field gets an explicit relative width so the
                row reads as one form instead of three mismatched ones. */}
            <TextField size="small" label="Key" value={newType.key} sx={{ width: { xs: '100%', sm: 140 } }}
              onChange={(e) => setNewType({ ...newType, key: e.target.value })} />
            <TextField size="small" label="Label" value={newType.label} sx={{ width: { xs: '100%', sm: 220 } }}
              onChange={(e) => setNewType({ ...newType, label: e.target.value })} />
            <TextField size="small" label="Prefix (e.g. REQ)" placeholder="PRJ-0001" value={newType.prefix}
              sx={{ width: { xs: '100%', sm: 140 } }}
              onChange={(e) => setNewType({ ...newType, prefix: e.target.value })} />
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              disabled={!newType.key.trim() || !newType.label.trim()} onClick={createType}>
              Create item type
            </Button>
          </Stack>
        )}
        {/* #886: same gate for the item-type list — an org with types still in flight
            showed the bare "create one" state, which reads as "this org has none". */}
        {(orgTypes.loading && !orgTypes.data) || (types.loading && !types.data)
          ? <LoadingNote text="Loading item types…" />
          : null}
        {/* Gated on `types` as well as `orgTypes`: the checkbox state comes from
            `attachedIds`, which is derived from `types`. Rendering the cards before it
            resolves ticks nothing, which reads as "no item types are attached to this
            project" — the same premature-empty-state defect one control over. */}
        {(types.loading && !types.data) ? null : (orgTypes.data || []).map((t) => {
          // #1037/#1042: eligible for a plain project admin only when the type is
          // non-system, attached to THIS project, and its `project_count` (from
          // orgTypes, backend-computed over every project — not an approximation
          // over ones this admin can see) is exactly 1. Org/super admins bypass
          // this entirely (canManageOrgWide).
          const projectAdminEligible = isProjectAdmin && !t.is_system && attachedIds.has(t.id) && t.project_count === 1;
          const canManageType = t.is_system ? isSuperAdmin : (canManageOrgWide || projectAdminEligible);
          return (
            <ItemTypeCard
              key={t.id}
              type={t}
              checked={attachedIds.has(t.id)}
              canToggleAttach={canManageOrgWide}
              canManage={canManageType}
              onToggleAttach={(on) => toggleAttach(t.id, on)}
              onRename={(label, prefix) => renameType(t.id, label, prefix)}
              onDelete={() => deleteType(t.id, t.label)}
              onAddField={(body) => addField(t.id, body)}
              onSaveField={(fieldId, patch) => saveField(t.id, fieldId, patch)}
              onDeleteField={(fieldId) => deleteField(t.id, fieldId)}
              onBulkSetShowInMain={(v) => bulkSetShowInMain(t.id, t.fields, v)}
              onRestamp={() => restampType(t.id, t.label, t.prefix)}
            />
          );
        })}
        {/* #388: re-stamp confirm + determinate progress dialog. Closable only
            when not mid-run, so a long operation isn't abandoned by a stray click. */}
        <Dialog open={!!restamp} maxWidth="xs" fullWidth
          onClose={() => { if (restamp && restamp.phase !== 'running') setRestamp(null); }}>
          <DialogTitle>Re-stamp existing items</DialogTitle>
          <DialogContent>
            {restamp && restamp.phase === 'confirm' && (
              <>
                <Typography variant="body2">
                  Re-stamp <strong>{restamp.total}</strong> existing item(s) of type
                  {' '}&quot;{restamp.typeLabel}&quot; with prefix
                  {' '}<strong>{restamp.prefix}</strong>? The old id keeps working as an alias.
                  Each item gets a History entry. This cannot be undone.
                </Typography>
                {/* #1085: keep the number by default; the admin can mint new numbers. */}
                <RadioGroup value={restamp.mode} data-testid="restamp-mode"
                  onChange={(e) => { const mode = e.target.value; setRestamp((s) => ({ ...s, mode })); }}>
                  <FormControlLabel value="keep_number" control={<Radio size="small" />}
                    label="Keep each number (REQ-0959 becomes CR-0959). A number already in use gets a new number." />
                  <FormControlLabel value="mint_new" control={<Radio size="small" />}
                    label="Give each item a new number from the project sequence." />
                </RadioGroup>
              </>
            )}
            {restamp && restamp.phase === 'running' && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }} data-testid="restamp-progress-label">
                  Re-stamping… {restamp.done} / {restamp.total}
                </Typography>
                <LinearProgress variant="determinate"
                  value={restamp.total ? Math.round((restamp.done / restamp.total) * 100) : 0} />
              </Box>
            )}
            {restamp && restamp.phase === 'done' && (
              <Alert severity="success" data-testid="restamp-done">
                Re-stamped {restamp.restamped} item(s) with prefix &quot;{restamp.prefix}&quot;.
              </Alert>
            )}
            {restamp && restamp.phase === 'error' && (
              <Alert severity="error">{restamp.error}</Alert>
            )}
          </DialogContent>
          <DialogActions>
            {restamp && restamp.phase === 'confirm' && (
              <>
                <Button onClick={() => setRestamp(null)}>Cancel</Button>
                <Button variant="contained" data-testid="restamp-confirm" onClick={runRestamp}>
                  Re-stamp {restamp.total}
                </Button>
              </>
            )}
            {restamp && (restamp.phase === 'done' || restamp.phase === 'error') && (
              <Button onClick={() => setRestamp(null)}>Close</Button>
            )}
          </DialogActions>
        </Dialog>
      </Section>

      {/* #943: in-app confirmation modal, replacing the native window.confirm the
          member-remove, item-type-delete and field-delete actions used. */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.onConfirm(); }}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        severity={confirm?.severity || null}
        confirmColor={confirm?.confirmColor || 'primary'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        testId={confirm?.testId || 'project-config-confirm-dialog'}
      />
    </Box>
  );
}

// --- Role control ----------------------------------------------------------
export function RoleControl() {
  const { activeOrg } = useAuth();
  const roles = useAsync(() => (activeOrg ? iamApi.roles(activeOrg) : Promise.resolve([])), [activeOrg]);
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Role control</Typography>
      <Section title="Roles">
        <Box className="scroll-x"><Table size="small">
          <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Scope</TableCell></TableRow></TableHead>
          <TableBody>{(roles.data || []).map((r) => <TableRow key={r.id}><TableCell>{r.name}</TableCell><TableCell>{r.is_system ? 'system' : 'custom'}</TableCell></TableRow>)}</TableBody>
        </Table></Box>
        <Typography variant="body2" sx={{ mt: 2 }}>The custom-role builder ships in a later phase (IAM-4).</Typography>
      </Section>
    </Box>
  );
}

// --- Platform console (super admin) ---------------------------------------
// Human labels for the embedder options. fastembed + local are both local 384-d
// models — fastembed is a lightweight ONNX runtime, local is heavyweight
// sentence-transformers/torch; azure/openrouter are remote.
const EMBEDDER_LABELS = {
  fastembed: 'fastembed — local ONNX, no key',
  local: 'local — sentence-transformers (heavy)',
  azure: 'azure — remote, higher quality',
  openrouter: 'openrouter — remote',
};

const PLATFORM_TAB_KEYS = ['approvals', 'allowlist', 'orgs', 'newsletter', 'embeddings',
  'users', 'billing', 'orphans', 'memory'];

export function PlatformConsole() {
  // #652: the worst of the three — nine tabs, five of them firing an admin query on
  // mount, including `adminOrgs()` and `allowlist()` for a console whose landing tab is
  // the signup queue.
  const [tab, tabLink] = useTabParam(PLATFORM_TAB_KEYS, '/account/platform');
  const pending = useAsync(() => iamApi.adminUsers('pending'), [], tab === 0);
  const allow = useAsync(() => iamApi.allowlist(), [], tab === 1);
  const orgs = useAsync(() => iamApi.adminOrgs(), [], tab === 2);
  const news = useAsync(() => iamApi.newsletter(), [], tab === 3);
  const [email, setEmail] = useState('');
  const embedding = useAsync(() => iamApi.getEmbedding(), [], tab === 4);
  const [embedder, setEmbedder] = useState('');
  const [busy, setBusy] = useState(null);          // 'save' | 'fix' | 'recreate'
  const [embedMsg, setEmbedMsg] = useState(null);  // { ok, text }
  useEffect(() => { if (embedding.data?.embedder) setEmbedder(embedding.data.embedder); }, [embedding.data]);
  // #647: Fix/Recreate block on the server until the whole backfill finishes (no
  // SSE) — poll the status endpoint on a separate request while one is in flight
  // so the panel can render live processed/total progress instead of just
  // sitting on "Fixing…"/"Recreating…" indefinitely.
  //
  // Depend on `embedding.reload` (a useCallback, so its identity only moves when the
  // active tab does) rather than on the whole `embedding` object, which useAsync rebuilds
  // as a fresh literal on every render — listing THAT would tear down and re-arm the
  // 1500ms interval on each poll response, so the timer would keep restarting and the
  // progress readout would stall exactly when it is being watched.
  const embeddingReload = embedding.reload;
  useEffect(() => {
    if (busy !== 'fix' && busy !== 'recreate') return undefined;
    const id = setInterval(() => { embeddingReload(); }, 1500);
    return () => clearInterval(id);
  }, [busy, embeddingReload]);
  const runEmbed = async (kind, fn) => {
    setBusy(kind); setEmbedMsg(null);
    try { const r = await fn(); setEmbedMsg({ ok: true, text: JSON.stringify(r) }); embedding.reload(); }
    catch (e) { setEmbedMsg({ ok: false, text: e?.response?.data?.detail || String(e.message || e) }); }
    finally { setBusy(null); }
  };
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Platform console</Typography>
      <Tabs
        value={tab}
        sx={{ mb: 2 }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label="Approvals" {...tabLink(0)} />
        <Tab label="Allowlist" {...tabLink(1)} />
        <Tab label="Organizations" {...tabLink(2)} />
        <Tab label="Newsletter" {...tabLink(3)} />
        <Tab label="Embeddings" {...tabLink(4)} />
        <Tab label="Users" {...tabLink(5)} />
        <Tab label="Billing" {...tabLink(6)} />
        <Tab label="Orphan items" {...tabLink(7)} />
        <Tab label="Memory" {...tabLink(8)} />
      </Tabs>
      {tab === 0 && (
        <Section title="Signup approval queue">
          <Box className="scroll-x"><Table size="small">
            <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Actions</TableCell></TableRow></TableHead>
            <TableBody>{(pending.data || []).map((u) => (
              <TableRow key={u.id}><TableCell>{u.email}</TableCell><TableCell>
                <Button size="small" onClick={() => iamApi.approveUser(u.id).then(pending.reload)}>Approve</Button>
                <Button size="small" color="error" onClick={() => iamApi.rejectUser(u.id).then(pending.reload)}>Reject</Button>
              </TableCell></TableRow>
            ))}</TableBody>
          </Table></Box>
        </Section>
      )}
      {tab === 1 && (
        <Section title="Email allowlist">
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button variant="contained" disabled={!email} onClick={() => iamApi.addAllowlist(email).then(() => { setEmail(''); allow.reload(); })}>Add</Button>
          </Stack>
          <List dense>{(allow.data || []).map((a) => (
            <ListItem key={a.email} secondaryAction={<IconButton onClick={() => iamApi.delAllowlist(a.email).then(allow.reload)}><DeleteIcon /></IconButton>}>
              <ListItemText primary={a.email} secondary={a.note} />
            </ListItem>
          ))}</List>
        </Section>
      )}
      {tab === 2 && (
        <Section title="Organizations">
          <Box className="scroll-x"><Table size="small">
            <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Plan</TableCell><TableCell>MCP allowed</TableCell></TableRow></TableHead>
            <TableBody>{(orgs.data || []).map((o) => (
              <TableRow key={o.id}><TableCell>{o.name}</TableCell><TableCell>{o.plan}</TableCell>
                <TableCell><Toggle label={`Allow MCP for ${o.name}`} on={!!o.caps?.mcp_connect?.allowed}
                  onChange={(v) => iamApi.setPlatformCap(o.id, 'mcp_connect', v).then(orgs.reload)} /></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></Box>
        </Section>
      )}
      {tab === 3 && (
        <Section title="Newsletter signups">
          <List dense>{(news.data || []).map((n) => <ListItem key={n.email}><ListItemText primary={n.email} secondary={n.source} /></ListItem>)}</List>
        </Section>
      )}
      {tab === 4 && (
        <Section title="Embeddings">
          <Typography variant="body2" sx={{ mb: 2, maxWidth: 700, color: 'text.secondary' }}>
            The semantic vector store powers search and the Vector Space view. Changing the embedder
            changes the vector dimension (fastembed/local are 384-d, azure 1536-d), so run
            {' '}<b>Recreate</b> afterwards to rebuild every vector with the new model.
          </Typography>

          {/* #647: provably reflect persisted (configured) vs actually-running (effective)
              state at all times — not just when they disagree — so "the dropdown shows
              fastembed" can never again be mistaken for "fastembed is what's running". */}
          <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }} useFlexGap>
            <Chip size="small" variant="outlined" label={`configured: ${embedding.data?.configured ?? '—'}`} />
            <Chip size="small" variant="outlined"
              color={embedding.data?.fallback_active ? 'warning' : 'success'}
              label={`effective: ${embedding.data?.embedder ?? '—'}`} />
          </Stack>

          {embedding.data?.fallback_active && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The selected embedder <b>{embedding.data.configured}</b> isn&apos;t available on this
              deployment (package not installed or key not set), so <b>{embedding.data.embedder}</b> is
              being used instead. Pick an available embedder below and Save.
            </Alert>
          )}

          {/* #647: external-API/write/search failures (e.g. an upstream rate limit)
              used to be visible only in the Render backend log — surface them here so
              the panel doesn't just look "slow" while every real embed is failing. */}
          {embedding.data?.embed_failures?.last_error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Embedding failures this process: {embedding.data.embed_failures.writes || 0} write,{' '}
              {embedding.data.embed_failures.searches || 0} search. Last error:{' '}
              <b>{embedding.data.embed_failures.last_error}</b>
              {embedding.data.embed_failures.last_at ? ` (${embedding.data.embed_failures.last_at})` : ''}
            </Alert>
          )}

          {/* #647: live processed/total for an in-flight Fix/Recreate, polled while
              `busy` is set — distinguishes "working normally but slow" from "stuck". */}
          {embedding.data?.progress?.active && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
                {embedding.data.progress.operation === 'recreate' ? 'Recreating' : 'Fixing'} embeddings…{' '}
                {embedding.data.progress.processed}
                {embedding.data.progress.total ? ` / ${embedding.data.progress.total}` : ''}
              </Typography>
              <LinearProgress
                variant={embedding.data.progress.total ? 'determinate' : 'indeterminate'}
                value={embedding.data.progress.total
                  ? Math.min(100, (embedding.data.progress.processed / embedding.data.progress.total) * 100)
                  : undefined} />
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap', mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }} useFlexGap>
            <Select size="small" value={embedder} onChange={(e) => setEmbedder(e.target.value)} sx={{ minWidth: { xs: '100%', sm: 280 }, maxWidth: '100%' }}>
              {(embedding.data?.options || []).map((o) => (
                <MenuItem key={o.id} value={o.id} disabled={o.available === false}>
                  {EMBEDDER_LABELS[o.id] || o.id} ({o.dim}-d){o.available === false ? ' — unavailable' : ''}
                </MenuItem>
              ))}
            </Select>
            <Button variant="contained" color="primary"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap', minWidth: 'fit-content' }}
              disabled={busy === 'save' || !embedder || embedder === embedding.data?.configured}
              onClick={() => runEmbed('save', () => iamApi.setEmbedding(embedder))}>
              {busy === 'save' ? 'Saving…' : 'Save embedder'}
            </Button>
          </Stack>

          {/* Coverage: scan the whole table → which records have a vector, and of which model/dim.
              #263: these numbers are PLATFORM-WIDE (all organisations) — label them as such,
              because the Fix/Recreate actions below are org-scoped and get their own numbers. */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1, alignItems: 'center' }} useFlexGap>
            <Chip size="small" color="success" variant="outlined"
              label={`embedded (platform-wide): ${embedding.data?.coverage?.embedded ?? '—'} / ${embedding.data?.coverage?.total ?? '—'}`} />
            <Chip size="small" color={(embedding.data?.coverage?.missing || 0) > 0 ? 'warning' : 'default'} variant="outlined"
              label={`no embedding (platform-wide): ${embedding.data?.coverage?.missing ?? '—'}`} />
            <Chip size="small" variant="outlined"
              label={`active: ${embedding.data?.embedder ?? '—'} (${embedding.data?.dim ?? '?'}-d)`} />
          </Stack>
          {/* Per model + dimension breakdown of what's actually stored. */}
          {(embedding.data?.coverage?.groups?.length > 0) && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1, alignItems: 'center' }} useFlexGap>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>stored vectors:</Typography>
              {embedding.data.coverage.groups.map((g) => (
                <Chip key={`${g.model}-${g.dim}`} size="small"
                  color={g.dim === embedding.data?.coverage?.active_dim ? 'primary' : 'default'}
                  label={`${g.count} × ${g.model} (${g.dim}-d)`} />
              ))}
            </Stack>
          )}
          {embedding.data?.coverage?.incompatible && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Stored vectors aren&apos;t all the active embedder&apos;s dimension ({embedding.data?.dim}-d).
              Semantic search and the Vector Space view only use vectors of the active dimension, so
              run <b>Recreate</b> to rebuild them all consistently.
            </Alert>
          )}

          {/* #263: Fix/Recreate go through the org-scoped /analysis/embeddings/{fix,recreate}
              endpoints — say so explicitly, with the ACTIVE-ORG numbers (not the platform-wide
              total above), so the number read next to an action matches what the action does. */}
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'text.secondary' }}>
            Fix missing and Recreate act on the active organisation only
            {embedding.data?.org_coverage
              ? ` — ${embedding.data.org_coverage.missing ?? '?'} missing of ${embedding.data.org_coverage.total ?? '?'} in this organisation`
              : ''}.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 2, mt: 1 }} useFlexGap>
            <Button variant="outlined" disabled={!!busy}
              onClick={() => runEmbed('fix', () => requirementsApi.fixEmbeddings())}>
              {busy === 'fix' ? 'Fixing…' : `Fix missing${embedding.data?.org_coverage ? ` (${embedding.data.org_coverage.missing} in this org)` : ''}`}
            </Button>
            <Button variant="outlined" color="warning" disabled={!!busy}
              onClick={() => runEmbed('recreate', () => requirementsApi.recreateEmbeddings())}>
              {busy === 'recreate' ? 'Recreating…' : 'Recreate (clear + rebuild this org)'}
            </Button>
          </Stack>
          {embedMsg && <Alert severity={embedMsg.ok ? 'success' : 'error'}>{embedMsg.text}</Alert>}
        </Section>
      )}
      {tab === 5 && <PlatformUsersTab />}
      {tab === 6 && <PlatformBillingTab />}
      {tab === 7 && <PlatformOrphanItemsTab />}
      {tab === 8 && <><PlatformMemoryTab /><ErrorTrackingPanel /></>}
    </Box>
  );
}

// #376: process-memory diagnostic — RSS (the number Render caps at 512 MB), GC
// stats, and (when tracing is on) the top Python allocation sites, so the operator
// can attribute a live OOM instead of guessing. Reads GET /api/iam/admin/memory
// (super-admin only); allocation attribution needs tracing, which is off by default
// (it carries overhead) — the button starts it, then Refresh shows the sites.
function PlatformMemoryTab() {
  const mem = useAsync(() => iamApi.getMemory({ top: 15 }), []);
  const [busy, setBusy] = useState(false);
  const [trim, setTrim] = useState(null);   // last malloc_trim result
  const d = mem.data;
  const overCap = typeof d?.rss_mb === 'number' && d.rss_mb >= 512;
  // RSS ≫ live Python heap → the difference is native memory + memory Python freed
  // but the allocator hasn't returned to the OS. A large "Trim" drop proves the latter.
  const gapMb = (typeof d?.rss_mb === 'number' && d?.tracing && typeof d?.traced_current_mb === 'number')
    ? Math.round(d.rss_mb - d.traced_current_mb) : null;
  const toggleTrace = async (on) => {
    setBusy(true);
    try { await iamApi.getMemory({ trace: on, top: 15 }); await mem.reload(); }
    finally { setBusy(false); }
  };
  const doTrim = async () => {
    setBusy(true); setTrim(null);
    try { const r = await iamApi.getMemory({ trim: true, top: 15 }); setTrim(r?.trim || null); await mem.reload(); }
    finally { setBusy(false); }
  };
  return (
    <Section title="Process memory (#376)">
      <Typography variant="body2" sx={{ mb: 2, maxWidth: 800, color: 'text.secondary' }}>
        Live memory of the backend process (single worker). <b>RSS</b> is the figure Render&apos;s
        512 MB tier caps. Allocation attribution needs <b>tracing</b> (Python <code>tracemalloc</code>),
        which is off by default because it adds overhead — start it, reproduce the workload
        (open the dashboard, run an export, use the agent), then Refresh to see the top sites.
        Note tracing only sees Python allocations made <i>after</i> it started; for the boot baseline,
        deploy with <code>MEMORY_PROFILING=1</code>. See <code>docs/deploy/MEMORY_PROFILING.md</code>.
      </Typography>
      {mem.loading && <Typography variant="body2">Loading…</Typography>}
      {mem.error && <Alert severity="error" sx={{ mb: 2 }}>{mem.error?.response?.data?.detail || String(mem.error)}</Alert>}
      {d && (
        <>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2, alignItems: 'center' }} useFlexGap>
            <Chip color={overCap ? 'error' : 'success'} variant="outlined"
              label={`RSS: ${d.rss_mb ?? '—'} MB${overCap ? ' — over 512 MB!' : ''}`} />
            <Chip size="small" variant="outlined" label={`tracked objects: ${d.gc_tracked_objects?.toLocaleString?.() ?? d.gc_tracked_objects ?? '—'}`} />
            <Chip size="small" variant="outlined" label={`GC gens: ${(d.gc_counts || []).join(' / ') || '—'}`} />
            <Chip size="small" color={d.tracing ? 'primary' : 'default'} variant="outlined"
              label={`tracing: ${d.tracing ? 'on' : 'off'}`} />
            {d.tracing && d.traced_current_mb != null && (
              <Chip size="small" variant="outlined" label={`traced heap: ${d.traced_current_mb} MB (peak ${d.traced_peak_mb ?? '—'})`} />
            )}
            <Chip size="small" variant="outlined" label={`source: ${d.rss_source || '—'}`} />
          </Stack>
          {gapMb != null && gapMb > 100 && (
            <Alert severity="info" sx={{ mb: 2, maxWidth: 800 }}>
              RSS ({d.rss_mb} MB) is far above the live Python heap ({d.traced_current_mb} MB) — a
              ~{gapMb} MB gap. That gap is native/C-extension memory <b>plus</b> memory Python has freed
              but the allocator hasn&apos;t returned to the OS (a small traced heap here means it&apos;s
              <b> not</b> a live-object leak). Click <b>Trim</b> to have glibc release the freed pages — a
              large drop confirms it was allocator-retained fragmentation.
            </Alert>
          )}
          {trim && (
            <Alert severity={trim.supported ? 'success' : 'warning'} sx={{ mb: 2, maxWidth: 800 }}>
              {trim.supported
                ? `Trim released ${trim.freed_mb ?? '?'} MB to the OS (RSS ${trim.rss_before_mb} → ${trim.rss_after_mb} MB). ${(trim.freed_mb || 0) > 50 ? 'A big drop = the RSS was mostly freed-but-unreturned allocator memory (fragmentation), not a leak.' : 'A small drop = the RSS is genuinely held (native memory or still-live objects).'}`
                : 'malloc_trim is not available on this platform (non-glibc) — no change.'}
            </Alert>
          )}
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', mb: 2 }} useFlexGap>
            <Button variant="outlined" disabled={busy || mem.loading} onClick={() => mem.reload()}>Refresh</Button>
            {d.tracing
              ? <Button variant="outlined" color="warning" disabled={busy} onClick={() => toggleTrace(false)}>Stop tracing</Button>
              : <Button variant="contained" disabled={busy} onClick={() => toggleTrace(true)}>Start tracing</Button>}
            <Button variant="outlined" disabled={busy} onClick={doTrim}>Trim (release to OS)</Button>
          </Stack>
          {d.tracing ? (
            (d.top_allocations || []).length > 0 ? (
              <Box className="scroll-x"><Table size="small">
                <TableHead><TableRow><TableCell>Allocation site</TableCell><TableCell align="right">Size (KB)</TableCell><TableCell align="right">Count</TableCell></TableRow></TableHead>
                <TableBody>{d.top_allocations.map((a) => (
                  <TableRow key={a.location}><TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{a.location}</TableCell>
                    <TableCell align="right">{a.size_kb?.toLocaleString?.() ?? a.size_kb}</TableCell>
                    <TableCell align="right">{a.count?.toLocaleString?.() ?? a.count}</TableCell></TableRow>
                ))}</TableBody>
              </Table></Box>
            ) : <Typography variant="body2" sx={{ color: 'text.secondary' }}>Tracing on — reproduce the workload, then Refresh to see the top allocation sites.</Typography>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Start tracing to attribute allocations to source lines.</Typography>
          )}
        </>
      )}
    </Section>
  );
}

// #1020 finding 3: an empty error tracker means one of two very different things —
// nothing has failed, or the DSN was never set on THIS service — and telling them apart
// used to require causing a real crash and watching the dashboard. Reads
// GET /api/iam/admin/error-tracking (super-admin); the button posts one deliberate
// exception and reports the id it was filed under, which exercises the whole path: SDK,
// DSN, network egress, project routing.
function ErrorTrackingPanel() {
  const et = useAsync(() => iamApi.getErrorTracking(), []);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const d = et.data;
  const send = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await iamApi.testErrorTracking();
      setResult(r?.sent
        ? { severity: 'success', text: `Sent. Event id ${r.event_id} — it should appear in the tracker within a minute.` }
        : { severity: 'warning', text: 'Nothing was sent: error tracking is not enabled on this service. Set SENTRY_DSN and redeploy.' });
      await et.reload?.();
    } catch (e) {
      setResult({ severity: 'error', text: e?.response?.data?.detail || String(e) });
    } finally { setBusy(false); }
  };
  return (
    <Section title="Error tracking">
      <Typography variant="body2" sx={{ mb: 2, maxWidth: 800, color: 'text.secondary' }}>
        Unhandled backend exceptions are reported to an external tracker when
        <code> SENTRY_DSN</code> is set on this service. A DSN that is present while the
        chip below still says the reporting is not on means the SDK failed to start — a
        malformed DSN or a missing package, both of which only log a warning nobody reads.
        Stack-frame local variables are deliberately <b>not</b> sent: they carry the
        caller&apos;s bearer token.
      </Typography>
      {et.loading && <Typography variant="body2">Loading…</Typography>}
      {et.error && <Alert severity="error" sx={{ mb: 2 }}>{et.error?.response?.data?.detail || String(et.error)}</Alert>}
      {d && (
        <>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 2, alignItems: 'center' }} useFlexGap>
            <Chip color={d.enabled ? 'success' : 'warning'} variant="outlined"
              label={`reporting: ${d.enabled ? 'on' : 'off'}`} />
            <Chip size="small" variant="outlined" label={`SENTRY_DSN: ${d.dsn_configured ? 'set' : 'not set'}`} />
            <Chip size="small" variant="outlined" label={`environment: ${d.environment || '—'}`} />
            <Chip size="small" variant="outlined" label={`release: ${d.release ? String(d.release).slice(0, 8) : '—'}`} />
          </Stack>
          {result && (
            <Alert severity={result.severity} sx={{ mb: 2, maxWidth: 800 }} onClose={() => setResult(null)}>
              {result.text}
            </Alert>
          )}
          <Button variant="contained" disabled={busy} onClick={send} data-testid="send-test-error-event">
            {busy ? 'Sending…' : 'Send a test error'}
          </Button>
        </>
      )}
    </Section>
  );
}

// #212 WS6: platform-wide user security actions — force-logout (session
// revocation) and GDPR erasure. Separate component so the user list only
// loads when the tab is opened.
function PlatformUsersTab() {
  const users = useAsync(() => iamApi.adminUsers(), []);
  const [msg, setMsg] = useState(null);
  const [busyUid, setBusyUid] = useState(null);
  // In-app confirmation modal (replaces window.confirm, #943). Holds the descriptor
  // for the pending action — { title, message, severity, confirmLabel, confirmColor,
  // testId, onConfirm } — or null when nothing is awaiting confirmation.
  const [confirm, setConfirm] = useState(null);
  const act = (uid, label, fn) => {
    setMsg(null); setBusyUid(uid);
    fn().then(() => { setMsg({ severity: 'success', text: `${label} done for ${uid}.` }); users.reload?.(); })
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || `${label} failed.` }))
      .finally(() => setBusyUid(null));
  };
  return (
    <Section title="Users — security actions">
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        <b>Sign out everywhere</b> revokes every existing session (the user signs in again to
        continue). <b>Erase</b> is irreversible GDPR erasure: identity anonymised, personal data
        (settings, keys, tokens, memberships, newsletter) removed, sessions revoked; the org&apos;s
        requirements content is retained. Both are audited.
      </Typography>
      {msg && <Alert sx={{ mb: 2 }} severity={msg.severity} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Box className="scroll-x"><Table size="small">
        <TableHead><TableRow><TableCell>Email</TableCell><TableCell>Status</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
        <TableBody>{(users.data || []).map((u) => (
          <TableRow key={u.id}>
            <TableCell>{u.email}</TableCell>
            <TableCell>{u.status}</TableCell>
            <TableCell align="right">
              <Button size="small" disabled={busyUid === u.id}
                onClick={() => setConfirm({
                  title: 'Sign out everywhere',
                  message: `Sign ${u.email} out everywhere?`,
                  confirmColor: 'error',
                  confirmLabel: 'Sign out everywhere',
                  testId: 'admin-revoke-sessions-confirm',
                  onConfirm: () => act(u.id, 'Session revocation', () => iamApi.adminRevokeSessions(u.id)),
                })}>Sign out everywhere</Button>
              <Button size="small" color="error" disabled={busyUid === u.id}
                onClick={() => setConfirm({
                  title: 'Permanently erase this user',
                  message: `Permanently ERASE ${u.email}? This anonymises the account and deletes their personal data. It cannot be undone.`,
                  severity: 'warning',
                  confirmColor: 'error',
                  confirmLabel: 'Erase',
                  testId: 'admin-erase-user-confirm',
                  onConfirm: () => act(u.id, 'Erasure', () => iamApi.adminEraseUser(u.id)),
                })}>Erase</Button>
            </TableCell>
          </TableRow>
        ))}</TableBody>
      </Table></Box>

      {/* #943: in-app confirmation modal, replacing the native window.confirm the
          session-revoke and erase actions used. */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.onConfirm(); }}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        severity={confirm?.severity || null}
        confirmColor={confirm?.confirmColor || 'primary'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        testId={confirm?.testId || 'platform-users-confirm-dialog'}
      />
    </Section>
  );
}

// #175: platform-wide view of requirements not attached to any project, with a
// bulk assign-to-project (org -> project cascading picker) and a bulk delete.
// Separate component so the (potentially cross-tenant) scan only runs when the
// tab is opened.
function PlatformOrphanItemsTab() {
  const orphans = useAsync(() => iamApi.adminOrphanItems(), []);
  const orgs = useAsync(() => iamApi.adminOrgs(), []);
  const [selected, setSelected] = useState(new Set());
  const [targetOrg, setTargetOrg] = useState('');
  const [targetProject, setTargetProject] = useState('');
  const [orgProjects, setOrgProjects] = useState([]);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  // In-app confirmation modal (replaces window.confirm, #943). Holds the descriptor
  // for the pending action — { title, message, severity, confirmLabel, confirmColor,
  // testId, onConfirm } — or null when nothing is awaiting confirmation.
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (!targetOrg) { setOrgProjects([]); setTargetProject(''); return; }
    // A super-admin sees every project in the chosen org via /me/projects
    // (its is_super_admin carve-out bypasses the usual membership filter).
    iamApi.myProjects(targetOrg).then(setOrgProjects).catch(() => setOrgProjects([]));
    setTargetProject('');
  }, [targetOrg]);

  const rows = orphans.data || [];
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)));
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const assign = () => {
    if (!targetProject || selected.size === 0) return;
    setMsg(null);
    setBusy(true);
    iamApi.assignOrphanItems(Array.from(selected), targetProject)
      .then((r) => {
        setMsg({ severity: 'success', text: `Assigned ${r.assigned} item(s) to the selected project.` });
        setSelected(new Set());
        orphans.reload?.();
      })
      .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not assign the selected items.' }))
      .finally(() => setBusy(false));
  };

  const del = () => {
    if (selected.size === 0) return;
    // #943: the guard is the in-app modal now, not a native window.confirm —
    // everything after the old early-return moved into `onConfirm`.
    setConfirm({
      title: 'Delete orphaned items',
      message: `Permanently delete ${selected.size} orphaned item(s)? This cannot be undone.`,
      severity: 'warning',
      confirmColor: 'error',
      confirmLabel: `Delete ${selected.size}`,
      testId: 'delete-orphan-items-confirm',
      onConfirm: () => {
        setMsg(null);
        setBusy(true);
        iamApi.deleteOrphanItems(Array.from(selected))
          .then((r) => {
            setMsg({ severity: 'success', text: `Deleted ${r.deleted} item(s).` });
            setSelected(new Set());
            orphans.reload?.();
          })
          .catch((e) => setMsg({ severity: 'error', text: e?.response?.data?.detail || 'Could not delete the selected items.' }))
          .finally(() => setBusy(false));
      },
    });
  };

  return (
    <Section title="Orphaned items">
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
        Requirements unreachable from the tree, across every organization — either not attached to
        any project (“No project”) or stranded in an archived project. Select some, then either
        assign them to an active project (pick its organization, then the project) or delete them
        permanently.
      </Typography>
      {msg && <Alert sx={{ mb: 2 }} severity={msg.severity} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap', mb: 2, alignItems: { xs: 'stretch', sm: 'flex-end' } }} useFlexGap>
        <Select size="small" displayEmpty value={targetOrg} onChange={(e) => setTargetOrg(e.target.value)}
          sx={{ minWidth: 200 }} data-testid="orphan-org-select">
          <MenuItem value="" disabled>Select organization…</MenuItem>
          {(orgs.data || []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
        </Select>
        <Select size="small" displayEmpty value={targetProject} onChange={(e) => setTargetProject(e.target.value)}
          sx={{ minWidth: 200 }} disabled={!targetOrg} data-testid="orphan-project-select">
          <MenuItem value="" disabled>Select project…</MenuItem>
          {orgProjects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name || p.title || p.id}</MenuItem>)}
        </Select>
        <Button size="small" variant="contained" disabled={!targetProject || selected.size === 0 || busy}
          onClick={assign} data-testid="orphan-assign">
          Assign {selected.size > 0 ? `(${selected.size})` : ''} to project
        </Button>
        <Button size="small" color="error" variant="outlined" disabled={selected.size === 0 || busy}
          onClick={del} data-testid="orphan-delete">
          Delete {selected.size > 0 ? `(${selected.size})` : ''}
        </Button>
      </Stack>
      <Box className="scroll-x"><Table size="small">
        <TableHead><TableRow>
          <TableCell padding="checkbox">
            <Checkbox checked={allChecked} indeterminate={selected.size > 0 && !allChecked} onChange={toggleAll} />
          </TableCell>
          <TableCell>Title</TableCell><TableCell>Type</TableCell><TableCell>Tenant</TableCell><TableCell>Why</TableCell><TableCell>Display ID</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell padding="checkbox">
                <Checkbox checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} />
              </TableCell>
              <TableCell>{r.title || '—'}</TableCell>
              <TableCell>{r.type_label || r.type_key || '—'}</TableCell>
              <TableCell>{r.tenant_name || r.tenant_id || '—'}</TableCell>
              <TableCell>{r.reason === 'archived_project'
                ? `Archived project${r.project_name ? `: ${r.project_name}` : ''}`
                : 'No project'}</TableCell>
              <TableCell>{r.display_id || '—'}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={6}><Typography variant="body2" sx={{ color: 'text.secondary' }}>No orphaned items.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table></Box>

      {/* #943: in-app confirmation modal, replacing the native window.confirm the
          bulk-delete action used. */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => { const c = confirm; setConfirm(null); if (c) c.onConfirm(); }}
        title={confirm?.title || ''}
        message={confirm?.message || ''}
        severity={confirm?.severity || null}
        confirmColor={confirm?.confirmColor || 'primary'}
        confirmLabel={confirm?.confirmLabel || 'Confirm'}
        testId={confirm?.testId || 'orphan-items-confirm-dialog'}
      />
    </Section>
  );
}
